import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../ui/alert-dialog';
import { toast } from 'sonner';
import BoardStatusManager from './BoardStatusManager';
import { useBoardStatuses } from './projectStatuses';
import ModernItemEditor from './ModernItemEditor';
import ProjectTaskList from './ProjectTaskList';
import { computeFormula } from '../../utils/formulaParser';
import { avatarColor, avatarInitials, displayName } from './avatarUtils';

const DEFAULT_STATUS_OPTIONS = ['feito','em_progresso','parado','working_on_it','stuck','done','to_do','pendente'];
const DEFAULT_PRIORITY_OPTIONS = ['alta','media','baixa','critica','nenhuma'];

export default function ModernProjectTable({
  board,
  groups,
  items,
  insights = null,
  advancedFilter = null,
  members,
  customColumns = [],
  boardListSummary = null,
  boardRefreshing = false,
  api,
  onItemPatched,
  onDataChange,
  onManageFields,
  currentUser,
  darkMode = false,
  onlyMyTasks = false,
  onToggleOnlyMyTasks,
  onClearAdvancedFilters
}) {
  const tableRef = useRef(null);
  const [newItem, setNewItem] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { itemId, field }
  const [editingValue, setEditingValue] = useState('');
  const [creatableInput, setCreatableInput] = useState('');
  const [selectedItemForComments, setSelectedItemForComments] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [itemEditorDirty, setItemEditorDirty] = useState(false);
  const [itemEditorSyncing, setItemEditorSyncing] = useState(false);
  const [showDiscardChangesDialog, setShowDiscardChangesDialog] = useState(false);
  const discardDialogClosedAtRef = useRef(0);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  useEffect(() => { if (!showAssignModal) setMemberSearch(''); }, [showAssignModal]);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskGroupId, setNewTaskGroupId] = useState('');
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewSubtaskModal, setShowNewSubtaskModal] = useState(false);
  const [subtaskParentId, setSubtaskParentId] = useState(null);
  const [selectedItemForAssign, setSelectedItemForAssign] = useState(null);
  const [dragItemId, setDragItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [dragSubId, setDragSubId] = useState(null);
  const [dragOverSubId, setDragOverSubId] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingFileUpload, setPendingFileUpload] = useState(null);

  // Column resize state — persisted to localStorage per board
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`nexo_colwidths_${board?.id}`);
      return saved ? JSON.parse(saved) : { name: 300, status: 130, person: 130, date: 130, priority: 130 };
    } catch { return { name: 300, status: 130, person: 130, date: 130, priority: 130 }; }
  });
  const resizingRef = useRef(null);

  // Column reorder state — persisted to localStorage per board
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(`nexo_colorder_${board?.id}`);
      return saved ? JSON.parse(saved) : ['name', 'status', 'person', 'date', 'priority'];
    } catch { return ['name', 'status', 'person', 'date', 'priority']; }
  });
  const [draggingCol, setDraggingCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  // Group rename state
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupTitle, setEditingGroupTitle] = useState('');
  const [deletingGroup, setDeletingGroup] = useState(null);

  // Status/priority options (can be extended by user)
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  const { statuses: boardStatuses, options: boardStatusKeys } = useBoardStatuses(api, board?.id);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [priorityOptions, setPriorityOptions] = useState(DEFAULT_PRIORITY_OPTIONS);

  // Sub-tasks state
  const [expandedSubtasks, setExpandedSubtasks] = useState({});
  const [subtasksData, setSubtasksData] = useState({});
  const [addingSubtask, setAddingSubtask] = useState(null);
  const [newSubtaskName, setNewSubtaskName] = useState('');

  const currentBoardMember = (members || []).find(member =>
    member.user_id === currentUser?.id || member.user_email === currentUser?.email
  );
  const currentBoardPermissions = currentBoardMember?.permissions || [];
  const assignableMembers = [...new Map((members || [])
    .filter(member => !member.status || member.status === 'active')
    .map(member => [member.user_id || member.id || member.user_email || member.email, member]))
    .values()];
  // Busca (sem acento/caixa) + ordem alfabética pelo nome exibido
  const normalizeSearch = (text) => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const memberDisplayName = (m) => displayName(
    m.user_name || m.name || m.user_email || m.email || 'Membro sem nome',
    m.user_id || m.id || m.user_email || m.email,
  );
  const filteredAssignableMembers = assignableMembers
    .filter(m => {
      const term = normalizeSearch(memberSearch.trim());
      return !term || normalizeSearch(`${memberDisplayName(m)} ${m.user_name || m.name || ''} ${m.user_email || m.email || ''}`).includes(term);
    })
    .sort((a, b) => memberDisplayName(a).localeCompare(memberDisplayName(b), 'pt-BR', { sensitivity: 'base' }));
  const canDeleteItems = currentUser?.role === 'admin' ||
    currentUser?.permissions?.includes('manage_system') ||
    ['owner', 'admin'].includes(currentBoardMember?.role) ||
    currentBoardPermissions.includes('all') ||
    currentBoardPermissions.includes('delete_items');
  const canManageGroups = currentUser?.role === 'admin' ||
    currentUser?.permissions?.includes('manage_system') ||
    ['owner', 'admin'].includes(currentBoardMember?.role) ||
    currentBoardPermissions.includes('all') ||
    currentBoardPermissions.includes('edit_board') ||
    currentBoardPermissions.includes('delete_groups');

  // ── Timer helpers ──────────────────────────────────────────────────────────
  const parseTimerValue = (raw) => {
    if (raw === null || raw === undefined) return { minutes: 0, timer_started_at: null };
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return { minutes: parseInt(raw.minutes) || 0, timer_started_at: raw.timer_started_at || null };
    }
    try {
      const d = JSON.parse(String(raw));
      if (d && typeof d === 'object') return { minutes: parseInt(d.minutes) || 0, timer_started_at: d.timer_started_at || null };
    } catch {}
    return { minutes: parseInt(raw) || 0, timer_started_at: null };
  };

  // Timer state: key = `${itemId}_${columnId}`, value = startTimestamp (ms)
  const [activeTimers, setActiveTimers] = useState({});
  // stoppedTimerValues: bridges the gap between stop click and DB refresh
  // { timerKey: finalMins } — keeps correct value displayed while onDataChange() is in-flight
  const [stoppedTimerValues, setStoppedTimerValues] = useState({});

  // Init activeTimers from backend data when items/columns change (survives navigation)
  useEffect(() => {
    const timers = {};
    const timerCols = customColumns.filter(c => c.type === 'time_tracking');
    items.forEach(item => {
      timerCols.forEach(col => {
        const raw = item.column_values?.[col.id];
        const { timer_started_at } = parseTimerValue(raw);
        if (timer_started_at) {
          timers[`${item.id}_${col.id}`] = new Date(timer_started_at).getTime();
        }
      });
    });
    if (Object.keys(timers).length > 0) {
      setActiveTimers(prev => {
        const merged = { ...prev };
        Object.entries(timers).forEach(([k, v]) => { if (!merged[k]) merged[k] = v; });
        return merged;
      });
    }
  }, [items, customColumns]);

  // SSE connection for real-time timer sync across tabs/users
  useEffect(() => {
    if (!board?.id) return;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    if (!token) return;
    const baseURL = api?.defaults?.baseURL || 'http://localhost:8000/api';
    const url = `${baseURL}/projects/boards/${board.id}/timer-stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'initial_state') {
          // Populate all currently running timers from backend on connect
          if (data.timers && data.timers.length > 0) {
            setActiveTimers(prev => {
              const next = { ...prev };
              data.timers.forEach(t => {
                const key = `${t.item_id}_${t.column_id}`;
                next[key] = new Date(t.timer_started_at).getTime();
              });
              return next;
            });
          }
        } else if (data.type === 'timer_start') {
          setActiveTimers(prev => ({ ...prev, [`${data.item_id}_${data.column_id}`]: new Date(data.timer_started_at).getTime() }));
        } else if (data.type === 'timer_stop') {
          setActiveTimers(prev => { const next = { ...prev }; delete next[`${data.item_id}_${data.column_id}`]; return next; });
        }
      } catch {}
    };
    es.onerror = () => { /* SSE will auto-reconnect */ };
    return () => es.close();
  }, [board?.id]);

  const deduplicateById = (arr) => {
    const seen = new Set();
    return (arr || []).filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };
  const allDependencyItems = useMemo(() => deduplicateById([...items, ...Object.values(subtasksData).flat()]), [items, subtasksData]);

  useEffect(() => {
    if (!insights?.groups?.length) return;
    setSubtasksData(previous => {
      const next = { ...previous };
      insights.groups.forEach(group => {
        (group.items || []).forEach(item => {
          const rolledUpSubtasks = deduplicateById(item.rollup?.subtasks || []);
          if (rolledUpSubtasks.length) {
            next[item.id] = rolledUpSubtasks;
          }
        });
      });
      return next;
    });
  }, [insights]);

  const toggleSubtasks = async (itemId) => {
    const willOpen = !expandedSubtasks[itemId];
    setExpandedSubtasks(prev => ({ ...prev, [itemId]: willOpen }));
    if (!willOpen || subtasksData[itemId]) return;
    try {
      const res = await api.get(`/projects/items/${itemId}/subtasks`);
      const deduped = deduplicateById(res.data);
      setSubtasksData(prev => ({ ...prev, [itemId]: deduped }));
    } catch {
      setExpandedSubtasks(prev => ({ ...prev, [itemId]: false }));
      toast.error('Erro ao carregar sub-tarefas');
    }
  };

  const handleAddSubtask = async (parentId) => {
    if (!newSubtaskName.trim()) return;
    try {
      const parentItem = items.find(i => i.id === parentId);
      await api.post(`/projects/items/${parentId}/subtasks`, {
        board_id: board.id, group_id: parentItem?.group_id || '', name: newSubtaskName.trim(),
        column_values: { estimated_time: '', difficulty: 'media' },
      });
      setNewSubtaskName('');
      setAddingSubtask(null);
      const res = await api.get(`/projects/items/${parentId}/subtasks`);
      setSubtasksData(prev => ({ ...prev, [parentId]: deduplicateById(res.data) }));
      setExpandedSubtasks(prev => ({ ...prev, [parentId]: true }));
      toast.success('Sub-tarefa criada!');
      return true;
    } catch { toast.error('Erro ao criar sub-tarefa'); return false; }
  };

  const openNewSubtaskModal = (parentId) => {
    setSubtaskParentId(parentId);
    setNewSubtaskName('');
    setShowNewSubtaskModal(true);
  };

  const handleCreateSubtaskFromModal = async () => {
    if (!subtaskParentId || !newSubtaskName.trim()) { toast.error('Informe o nome da subtarefa'); return; }
    const created = await handleAddSubtask(subtaskParentId);
    if (created) setShowNewSubtaskModal(false);
  };

  // Generate dynamic grid columns based on colWidths and colOrder state
  const getDynamicGridCols = () => {
    const customCount = customColumns?.length || 0;
    const baseCols = colOrder.map(k => `${colWidths[k] || 130}px`).join(' ');
    const customCols = Array(customCount).fill('150px').join(' ');
    const actionCol = '60px';
    return `${baseCols} ${customCols} ${actionCol}`.trim();
  };

  // Column drag-to-reorder handlers
  const handleColDragStart = (e, colKey) => {
    setDraggingCol(colKey);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleColDragOver = (e, colKey) => {
    e.preventDefault();
    if (colKey === draggingCol) return;
    setDragOverCol(colKey);
  };
  const handleColDrop = (e, targetCol) => {
    e.preventDefault();
    if (!draggingCol || targetCol === draggingCol) return;
    setColOrder(prev => {
      const from = prev.indexOf(draggingCol);
      const to = prev.indexOf(targetCol);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, draggingCol);
      try { localStorage.setItem(`nexo_colorder_${board?.id}`, JSON.stringify(next)); } catch {}
      return next;
    });
    setDraggingCol(null);
    setDragOverCol(null);
  };
  const handleColDragEnd = () => { setDraggingCol(null); setDragOverCol(null); };

  // Group rename handler
  const handleRenameGroup = async (groupId) => {
    if (!editingGroupTitle.trim()) { setEditingGroupId(null); return; }
    try {
      await api.put(`/projects/groups/${groupId}`, { title: editingGroupTitle.trim() });
      onDataChange();
      toast.success('Grupo renomeado!');
    } catch { toast.error('Erro ao renomear grupo'); }
    setEditingGroupId(null);
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      await api.delete(`/projects/groups/${deletingGroup.id}`);
      onDataChange();
      toast.success('Grupo excluído com sucesso!');
    } catch {
      toast.error('Erro ao excluir grupo');
    }
    setDeletingGroup(null);
  };

  // Column resize handlers
  const handleResizeStart = useCallback((col, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths[col];
    resizingRef.current = { col, startX, startWidth };

    const onMouseMove = (me) => {
      if (!resizingRef.current) return;
      const delta = me.clientX - resizingRef.current.startX;
      const newWidth = Math.max(80, resizingRef.current.startWidth + delta);
      setColWidths(prev => ({ ...prev, [resizingRef.current.col]: newWidth }));
    };
    const onMouseUp = () => {
      if (resizingRef.current) {
        setColWidths(prev => {
          try { localStorage.setItem(`nexo_colwidths_${board?.id}`, JSON.stringify(prev)); } catch {}
          return prev;
        });
      }
      resizingRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  // Inline edit save
  const saveInlineEdit = useCallback(async (itemId, field, value) => {
    try {
      const mainItem = items.find(i => i.id === itemId);
      const subItem = !mainItem
        ? Object.values(subtasksData).flat().find(s => s.id === itemId)
        : null;
      const item = mainItem || subItem;
      const updatedValues = { ...(item?.column_values || {}), [field]: value };
      await api.put(`/projects/items/${itemId}`, { column_values: updatedValues });
      setEditingCell(null);
      setEditingValue('');
      setCreatableInput('');
      if (subItem) {
        // Atualiza subtasksData localmente sem rebuscar tudo
        setSubtasksData(prev => {
          const next = { ...prev };
          for (const parentId of Object.keys(next)) {
            const idx = next[parentId].findIndex(s => s.id === itemId);
            if (idx !== -1) {
              next[parentId] = [...next[parentId]];
              next[parentId][idx] = {
                ...next[parentId][idx],
                column_values: updatedValues
              };
              break;
            }
          }
          return next;
        });
      } else {
        onDataChange();
      }
    } catch {
      toast.error('Erro ao salvar');
    }
  }, [api, items, onDataChange, subtasksData]);

  const openInlineEdit = (e, itemId, field, currentValue) => {
    e.stopPropagation();
    setEditingCell({ itemId, field });
    setEditingValue(currentValue || '');
    setCreatableInput('');
  };

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') { setEditingCell(null); setEditingValue(''); } };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Close editingCell when clicking outside the table (e.g. on a Projects.js dropdown trigger)
  useEffect(() => {
    if (!editingCell) return;
    const handleOutside = (e) => {
      if (tableRef.current && !tableRef.current.contains(e.target)) {
        setEditingCell(null);
        setEditingValue('');
        setCreatableInput('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [editingCell]);

  // Effect para forçar re-render quando board muda
  useEffect(() => {}, [board?.id]);

  // Sistema de cores para grupos
  const getGroupColor = (index) => {
    const colors = [
      { bg: 'bg-blue-50', border: '#3B82F6', text: 'text-blue-800', icon: '📋' },
      { bg: 'bg-green-50', border: '#10B981', text: 'text-green-800', icon: '✅' },
      { bg: 'bg-purple-50', border: '#8B5CF6', text: 'text-purple-800', icon: '🚀' },
      { bg: 'bg-orange-50', border: '#F97316', text: 'text-orange-800', icon: '⚡' },
      { bg: 'bg-pink-50', border: '#EC4899', text: 'text-pink-800', icon: '💎' },
      { bg: 'bg-indigo-50', border: '#6366F1', text: 'text-indigo-800', icon: '🎯' },
      { bg: 'bg-teal-50', border: '#14B8A6', text: 'text-teal-800', icon: '🌊' },
      { bg: 'bg-red-50', border: '#EF4444', text: 'text-red-800', icon: '🔥' }
    ];
    return colors[index % colors.length];
  };

  // Definir cores modernas para status
  const getStatusStyle = (status) => {
    const statusColors = {
      'feito': { bg: 'bg-green-500', text: 'text-white', label: 'Feito' },
      'em_progresso': { bg: 'bg-blue-500', text: 'text-white', label: 'Em progresso' },
      'parado': { bg: 'bg-red-500', text: 'text-white', label: 'Parado' },
      'working_on_it': { bg: 'bg-orange-500', text: 'text-white', label: 'Working on it' },
      'stuck': { bg: 'bg-red-400', text: 'text-white', label: 'Stuck' },
      'done': { bg: 'bg-green-500', text: 'text-white', label: 'Done' },
      'to_do': { bg: 'bg-gray-400', text: 'text-white', label: 'To Do' },
      'pendente': { bg: 'bg-yellow-500', text: 'text-white', label: 'Pendente' }
    };
    return statusColors[status] || { bg: 'bg-gray-300', text: 'text-gray-800', label: status || 'Sem status' };
  };

  // Agrupar itens por grupo — dedup em grupos E em items para evitar chaves duplicadas
  const seenGroupIds = new Set();
  const groupedItems = useMemo(() => {
    const uniqueGroups = (groups || []).filter(g => {
      if (!g?.id || seenGroupIds.has(g.id)) return false;
      seenGroupIds.add(g.id);
      return true;
    });
    const seenIds = new Set();
    return uniqueGroups.map(group => ({
      ...group,
      items: (items || []).filter(item => {
        if (item.group_id !== group.id) return false;
        if (seenIds.has(item.id)) return false;
        seenIds.add(item.id);
        return true;
      })
    }));
  }, [groups, items]);

  const handleAddItem = async (groupId, itemName) => {
    if (!itemName.trim()) return;
    
    try {
      await api.post('/projects/items', {
        board_id: board.id,
        group_id: groupId,
        name: itemName,
        column_values: { estimated_time: '', difficulty: 'media' },
        position: 0
      });
      
      setNewItem('');
      onDataChange();
      toast.success('Item adicionado com sucesso!');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Erro ao adicionar item');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!item || !window.confirm(`Excluir a atividade "${item.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/projects/items/${item.id}`);
      setSubtasksData(previous => Object.fromEntries(
        Object.entries(previous)
          .filter(([parentId]) => parentId !== item.id)
          .map(([parentId, subtasks]) => [parentId, subtasks.filter(subtask => subtask.id !== item.id)])
      ));
      toast.success('Atividade excluída com sucesso!');
      onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir atividade');
    }
  };

  const getTimerDisplayMinutes = useCallback((item, column) => {
    const timerKey = `${item.id}_${column.id}`;
    const { minutes: storedMinutes } = parseTimerValue(item.column_values?.[column.id]);
    if (activeTimers[timerKey]) {
      return storedMinutes + Math.max(0, Math.floor((Date.now() - activeTimers[timerKey]) / 60000));
    }
    return stoppedTimerValues[timerKey] ?? storedMinutes;
  }, [activeTimers, stoppedTimerValues]);

  const handleCellEdit = async (itemId, columnId, value) => {
    try {
      const item = items.find(i => i.id === itemId);
      const updatedColumnValues = { ...item.column_values, [columnId]: value };
      
      await api.put(`/projects/items/${itemId}`, {
        column_values: updatedColumnValues
      });
      
      setEditingCell(null);
      onDataChange();
      toast.success('Item atualizado!');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Erro ao atualizar item');
    }
  };

  const handleItemClick = useCallback((item) => {
    setSelectedItemForComments(item);
    setItemEditorDirty(false);
    setShowCommentsModal(true);
    if (item?.parent_id || subtasksData[item.id]) return;
    api.get(`/projects/items/${item.id}/subtasks`)
      .then(res => setSubtasksData(prev => ({ ...prev, [item.id]: deduplicateById(res.data) })))
      .catch(() => {});
  }, [api, subtasksData]);

  const closeCommentsModal = useCallback(() => {
    setShowCommentsModal(false);
    setSelectedItemForComments(null);
    setItemEditorDirty(false);
    setItemEditorSyncing(false);
    setShowDiscardChangesDialog(false);
  }, []);

  const requestCommentsModalClose = useCallback(() => {
    if (itemEditorDirty) {
      setShowDiscardChangesDialog(true);
      return;
    }
    closeCommentsModal();
  }, [closeCommentsModal, itemEditorDirty]);

  const isTimerRunning = useCallback((item, column) => Boolean(activeTimers[`${item.id}_${column.id}`]), [activeTimers]);

  const handleAssignMember = useCallback((itemId) => {
    if (assignableMembers.length === 0) {
      toast.error('Nenhum membro encontrado neste quadro. Adicione membros primeiro.');
      return;
    }

    // Procura primeiro nos itens pai, depois nas sub-tarefas
    const item = items.find(i => i.id === itemId)
      || Object.values(subtasksData).flat().find(s => s.id === itemId);
    if (!item) {
      toast.error('Item não encontrado');
      return;
    }

    setSelectedItemForAssign(item);
    setShowAssignModal(true);
  }, [assignableMembers, items, subtasksData]);

  const handleStatusChange = useCallback((item, status) => saveInlineEdit(item.id, 'status', status), [saveInlineEdit]);
  const handleInlineFieldChange = useCallback((item, field, value) => saveInlineEdit(item.id, field, value), [saveInlineEdit]);
  const handlePriorityChange = useCallback((item, priority) => saveInlineEdit(item.id, 'priority', priority), [saveInlineEdit]);

  const removeMemberFromItem = async (personIdToRemove, personName) => {
    if (!selectedItemForAssign) return;
    try {
      const currentPersons = selectedItemForAssign.column_values?.persons || [];
      const updatedPersons = currentPersons.filter(p => p.id !== personIdToRemove && p.name !== personName);
      const lastPerson = updatedPersons[updatedPersons.length - 1];
      const updatedColumnValues = {
        ...selectedItemForAssign.column_values,
        person: lastPerson?.name || '',
        person_id: lastPerson?.id || '',
        persons: updatedPersons
      };
      await api.put(`/projects/items/${selectedItemForAssign.id}`, { column_values: updatedColumnValues });
      setSelectedItemForAssign(prev => ({ ...prev, column_values: updatedColumnValues }));
      toast.success(`${personName} removido dos responsáveis`);

      // Se é sub-tarefa, atualiza subtasksData localmente; senão faz reload do quadro
      const isSubtask = Object.values(subtasksData).some(
        subs => subs.some(s => s.id === selectedItemForAssign.id)
      );
      if (isSubtask) {
        setSubtasksData(prev => {
          const next = { ...prev };
          for (const parentId of Object.keys(next)) {
            const idx = next[parentId].findIndex(s => s.id === selectedItemForAssign.id);
            if (idx !== -1) {
              next[parentId] = [...next[parentId]];
              next[parentId][idx] = { ...next[parentId][idx], column_values: updatedColumnValues };
              break;
            }
          }
          return next;
        });
      } else {
        onDataChange();
      }
    } catch {
      toast.error('Erro ao remover responsável');
    }
  };

  const assignMemberToItem = async (memberId) => {
    if (!selectedItemForAssign || !memberId) return;

    // Buscar membro usando campos corretos da API
    const selectedMember = assignableMembers.find(m =>
      m.id === memberId || 
      m.user_id === memberId || 
      m.user_email === memberId || 
      m.email === memberId
    );
    
    if (!selectedMember) {
      toast.error('Membro não encontrado');
      return;
    }

    try {
      // Usar campos corretos da API para salvar
      const memberName = selectedMember.user_name || selectedMember.name || selectedMember.user_email || selectedMember.email;
      const memberIdValue = selectedMember.user_id || selectedMember.id || selectedMember.user_email || selectedMember.email;
      
      // Verificar se já tem responsáveis atribuídos
      const currentPersons = selectedItemForAssign.column_values?.persons || [];
      const currentPerson = selectedItemForAssign.column_values?.person;
      
      // Criar lista consolidada de responsáveis
      let updatedPersons = [...currentPersons];
      
      // Se há um responsável principal, incluir na lista
      if (currentPerson && currentPerson !== '' && currentPerson !== 'undefined') {
        const existsInPersons = updatedPersons.find(p => p.name === currentPerson || p.id === selectedItemForAssign.column_values?.person_id);
        if (!existsInPersons) {
          updatedPersons.push({ 
            name: currentPerson, 
            id: selectedItemForAssign.column_values?.person_id || currentPerson 
          });
        }
      }
      
      // Verificar se o membro já está na lista
      const memberExists = updatedPersons.find(p => p.id === memberIdValue || p.name === memberName);
      if (memberExists) {
        toast.warning(`${memberName} já é responsável por esta tarefa!`);
        setShowAssignModal(false);
        return;
      }
      
      // Adicionar novo responsável
      updatedPersons.push({ name: memberName, id: memberIdValue });
      
      const updatedColumnValues = { 
        ...selectedItemForAssign.column_values, 
        person: memberName, // Manter o último atribuído como principal
        person_id: memberIdValue,
        persons: updatedPersons // Lista completa de responsáveis
      };

      await api.put(`/projects/items/${selectedItemForAssign.id}`, {
        column_values: updatedColumnValues
      });

      setShowAssignModal(false);
      setSelectedItemForAssign(null);

      // Se é sub-tarefa, atualiza subtasksData localmente; senão faz reload do quadro
      const isSubtask = Object.values(subtasksData).some(
        subs => subs.some(s => s.id === selectedItemForAssign.id)
      );
      if (isSubtask) {
        setSubtasksData(prev => {
          const next = { ...prev };
          for (const parentId of Object.keys(next)) {
            const idx = next[parentId].findIndex(s => s.id === selectedItemForAssign.id);
            if (idx !== -1) {
              next[parentId] = [...next[parentId]];
              next[parentId][idx] = { ...next[parentId][idx], column_values: updatedColumnValues };
              break;
            }
          }
          return next;
        });
      } else {
        onDataChange();
      }

      if (updatedPersons.length === 1) {
        toast.success(`${memberName} atribuído como responsável!`);
      } else {
        toast.success(`${memberName} adicionado aos responsáveis! (${updatedPersons.length} responsáveis)`);
      }
    } catch (error) {
      console.error('Error assigning member:', error);
      toast.error('Erro ao atribuir responsável');
    }
  };

  const handleCustomColumnClick = async (e, item, column) => {
    e.stopPropagation(); // Evitar que abra o modal de comentários
    
    const columnType = (column.type || '').toLowerCase();
    
    console.log(`🔧 Editando coluna "${column.title}" (tipo: ${columnType})`);
    
    switch (columnType) {
      case 'progress':
      case 'progresso':
        const currentProgress = item.column_values?.[column.id] || 0;
        const newProgress = prompt(
          `Digite a porcentagem de progresso (0-100):\n\nAtual: ${currentProgress}%`,
          currentProgress
        );
        
        if (newProgress !== null) {
          const progressValue = parseInt(newProgress);
          if (!isNaN(progressValue) && progressValue >= 0 && progressValue <= 100) {
            await updateItemColumn(item, column, progressValue);
          } else {
            toast.error('Digite um valor entre 0 e 100');
          }
        }
        break;

      case 'checkbox':
      case 'check':
        const currentValue = Boolean(item.column_values?.[column.id]);
        await updateItemColumn(item, column, !currentValue);
        break;

      case 'text':
      case 'texto':
        const currentText = item.column_values?.[column.id] || '';
        const newText = prompt(`Digite o texto para ${column.title}:`, currentText);
        
        if (newText !== null) {
          await updateItemColumn(item, column, newText);
        }
        break;

      case 'number':
      case 'numero':
        const currentNumber = item.column_values?.[column.id] || 0;
        const newNumber = prompt(`Digite o número para ${column.title}:`, currentNumber);
        
        if (newNumber !== null) {
          const numValue = parseFloat(newNumber);
          if (!isNaN(numValue)) {
            await updateItemColumn(item, column, numValue);
          } else {
            toast.error('Digite um número válido');
          }
        }
        break;

      case 'rating':
        const currentRating = parseInt(item.column_values?.[column.id]) || 0;
        const newRating = prompt(`Avaliação para "${column.title}" (1-5 estrelas):\nAtual: ${'★'.repeat(currentRating)}${'☆'.repeat(5-currentRating)}`, currentRating);
        if (newRating !== null) {
          const ratingNum = parseInt(newRating);
          if (!isNaN(ratingNum) && ratingNum >= 0 && ratingNum <= 5) {
            await updateItemColumn(item, column, ratingNum);
          } else {
            toast.error('Digite um valor entre 0 e 5');
          }
        }
        break;

      case 'vote':
        const currentVotes = parseInt(item.column_values?.[column.id]) || 0;
        await updateItemColumn(item, column, currentVotes + 1);
        break;

      case 'time_tracking':
        // Timer is handled inline via handleTimerToggle — clicking the cell does nothing extra
        break;

      case 'formula':
        // Formula columns are read-only (computed), no click action
        toast.info('Colunas de fórmula são somente leitura e calculadas automaticamente.');
        break;

      case 'file':
        // Trigger hidden file input for upload
        setPendingFileUpload({ item, column });
        if (fileInputRef.current) fileInputRef.current.click();
        break;

      case 'date':
      case 'data':
        const currentDate = item.column_values?.[column.id];
        const currentDateStr = currentDate ? new Date(currentDate).toISOString().split('T')[0] : '';
        
        // Create date input dialog
        const dateInput = document.createElement('input');
        dateInput.type = 'date';
        dateInput.value = currentDateStr;
        dateInput.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 9999;
          padding: 10px;
          border: 2px solid #3b82f6;
          border-radius: 8px;
          font-size: 16px;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        `;
        
        const backdrop = document.createElement('div');
        backdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 9998;
        `;
        
        document.body.appendChild(backdrop);
        document.body.appendChild(dateInput);
        dateInput.focus();
        
        // Safe cleanup function with existence checks
        const cleanup = () => {
          try {
            if (backdrop && backdrop.parentNode === document.body) {
              document.body.removeChild(backdrop);
            }
            if (dateInput && dateInput.parentNode === document.body) {
              document.body.removeChild(dateInput);
            }
          } catch (error) {
            console.warn('Cleanup error (non-critical):', error);
          }
        };
        
        // Prevent multiple event listeners
        let isProcessing = false;
        
        dateInput.addEventListener('change', async () => {
          if (isProcessing) return;
          isProcessing = true;
          
          if (dateInput.value) {
            await updateItemColumn(item, column, dateInput.value);
          }
          cleanup();
        });
        
        backdrop.addEventListener('click', () => {
          if (!isProcessing) {
            cleanup();
          }
        });
        
        dateInput.addEventListener('blur', () => {
          if (!isProcessing) {
            setTimeout(cleanup, 200); // Longer delay to prevent conflicts
          }
        });
        
        // Escape key support
        dateInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !isProcessing) {
            cleanup();
          }
        });
        break;

      case 'status':
        // Handle status column editing - same as dropdown but with status-specific options
        let statusOptions = [];
        if (column.settings && column.settings.options && Array.isArray(column.settings.options)) {
          statusOptions = column.settings.options;
        } else if (column.options && Array.isArray(column.options)) {
          statusOptions = column.options;
        } else if (column.options && typeof column.options === 'string') {
          statusOptions = column.options.split(/[\\,|]/).filter(opt => opt.trim());
        } else {
          // Default status options if none defined
          statusOptions = ['Pendente', 'Em Progresso', 'Concluído', 'Parado'];
        }
        
        const currentStatusValue = item.column_values?.[column.id] || '';
        
        console.log(`Editando status "${column.title}" com opções:`, statusOptions);
        
        let statusOptionsHtml = statusOptions.map((option) => 
          `<option value="${option}" ${option === currentStatusValue ? 'selected' : ''}>${option}</option>`
        ).join('');
        
        const statusSelectInput = document.createElement('select');
        statusSelectInput.innerHTML = `<option value="">Selecione um status</option>${statusOptionsHtml}`;
        statusSelectInput.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 9999;
          padding: 10px;
          border: 2px solid #3b82f6;
          border-radius: 8px;
          font-size: 16px;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          min-width: 200px;
        `;
        
        const statusBackdrop = document.createElement('div');
        statusBackdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 9998;
        `;
        
        document.body.appendChild(statusBackdrop);
        document.body.appendChild(statusSelectInput);
        statusSelectInput.focus();
        
        // Safe cleanup function for status
        const statusCleanup = () => {
          try {
            if (statusBackdrop && statusBackdrop.parentNode === document.body) {
              document.body.removeChild(statusBackdrop);
            }
            if (statusSelectInput && statusSelectInput.parentNode === document.body) {
              document.body.removeChild(statusSelectInput);
            }
          } catch (error) {
            console.warn('Status cleanup error (non-critical):', error);
          }
        };
        
        // Prevent multiple processing
        let isStatusProcessing = false;
        
        statusSelectInput.addEventListener('change', async () => {
          if (isStatusProcessing) return;
          isStatusProcessing = true;
          
          await updateItemColumn(item, column, statusSelectInput.value);
          statusCleanup();
        });
        
        statusBackdrop.addEventListener('click', () => {
          if (!isStatusProcessing) {
            statusCleanup();
          }
        });
        
        statusSelectInput.addEventListener('blur', () => {
          if (!isStatusProcessing) {
            setTimeout(statusCleanup, 200);
          }
        });
        
        // Escape key support for status
        statusSelectInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !isStatusProcessing) {
            statusCleanup();
          }
        });
        break;

      case 'dropdown':
      case 'lista_suspensa':
      case 'select':
        // Get dropdown options from column settings - USE REAL OPTIONS FROM BACKEND
        let realOptions = [];
        if (column.settings && column.settings.options && Array.isArray(column.settings.options)) {
          realOptions = column.settings.options;
        } else if (column.options && Array.isArray(column.options)) {
          realOptions = column.options;
        } else if (column.options && typeof column.options === 'string') {
          // Parse string options like "BAD\\GOOD" or "option1,option2,option3"
          realOptions = column.options.split(/[\\,|]/).filter(opt => opt.trim());
        } else {
          // Fallback only if no options are defined
          realOptions = ['Opção 1', 'Opção 2', 'Opção 3'];
        }
        
        const currentDropdownValue = item.column_values?.[column.id] || '';
        
        console.log(`Editando dropdown "${column.title}" com opções:`, realOptions);
        
        let optionsHtml = realOptions.map((option) => 
          `<option value="${option}" ${option === currentDropdownValue ? 'selected' : ''}>${option}</option>`
        ).join('');
        
        const selectInput = document.createElement('select');
        selectInput.innerHTML = `<option value="">Selecione uma opção</option>${optionsHtml}`;
        selectInput.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 9999;
          padding: 10px;
          border: 2px solid #3b82f6;
          border-radius: 8px;
          font-size: 16px;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          min-width: 200px;
        `;
        
        const dropdownBackdrop = document.createElement('div');
        dropdownBackdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 9998;
        `;
        
        document.body.appendChild(dropdownBackdrop);
        document.body.appendChild(selectInput);
        selectInput.focus();
        
        // Safe cleanup function for dropdown
        const dropdownCleanup = () => {
          try {
            if (dropdownBackdrop && dropdownBackdrop.parentNode === document.body) {
              document.body.removeChild(dropdownBackdrop);
            }
            if (selectInput && selectInput.parentNode === document.body) {
              document.body.removeChild(selectInput);
            }
          } catch (error) {
            console.warn('Dropdown cleanup error (non-critical):', error);
          }
        };
        
        // Prevent multiple processing
        let isDropdownProcessing = false;
        
        selectInput.addEventListener('change', async () => {
          if (isDropdownProcessing) return;
          isDropdownProcessing = true;
          
          await updateItemColumn(item, column, selectInput.value);
          dropdownCleanup();
        });
        
        dropdownBackdrop.addEventListener('click', () => {
          if (!isDropdownProcessing) {
            dropdownCleanup();
          }
        });
        
        selectInput.addEventListener('blur', () => {
          if (!isDropdownProcessing) {
            setTimeout(dropdownCleanup, 200);
          }
        });
        
        // Escape key support for dropdown
        selectInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !isDropdownProcessing) {
            dropdownCleanup();
          }
        });
        break;

      case 'person':
        // Handle person assignment columns - fetch real board members
        try {
          // Get board ID from props (passed from Projects.js)
          const currentBoardId = board?.id;

          if (!currentBoardId) {
            console.error('Board ID não encontrado, usando fallback');
            // Fallback to simple input if we can't get board ID
            const currentPersonValue = item.column_values?.[column.id] || '';
            const newPersonValue = prompt(`Atribuir pessoa para ${column.title}:\n\nDigite o ID ou nome da pessoa:`, currentPersonValue);
            
            if (newPersonValue !== null) {
              await updateItemColumn(item, column, newPersonValue.trim());
            }
            break;
          }

          console.log(`Buscando membros para board ID: ${currentBoardId}`);

          // Fetch board members
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/boards/${currentBoardId}/members`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch board members');
          }

          const boardMembers = await response.json();
          
          // Filter and format members
          const activeMembers = boardMembers
            .filter(member => member.status === 'active' || member.status === 'pending')
            .map(member => ({
              id: member.user_id,
              name: member.user_name || member.user_email.split('@')[0],
              email: member.user_email,
              avatar: member.user_name === 'Administrador do Sistema' ? '👨‍💼' : 
                     member.user_name?.includes('Fernando') ? '🧑‍💻' : 
                     member.user_name?.includes('Debora') ? '👩‍💻' : '👤',
              role: member.role,
              status: member.status
            }));

          // Filter to only available members (from column settings)
          const availableMembers = column.settings?.members 
            ? activeMembers.filter(m => column.settings.members.includes(m.id))
            : activeMembers;

          if (availableMembers.length === 0) {
            alert('Nenhum membro disponível para esta coluna. Configure os membros nas configurações da coluna.');
            break;
          }

          const currentPersonValue = item.column_values?.[column.id] || '';
          
          console.log(`Editando person "${column.title}" - Membros disponíveis:`, availableMembers);
          
          // Create member selection dropdown
          let personOptionsHtml = availableMembers.map((member) => {
            const isSelected = member.id === currentPersonValue;
            return `<option value="${member.id}" ${isSelected ? 'selected' : ''}>
                     ${member.avatar} ${member.name} (${member.email})
                   </option>`;
          }).join('');
          
          const personSelectInput = document.createElement('select');
          personSelectInput.innerHTML = `
            <option value="">Nenhuma pessoa atribuída</option>
            ${personOptionsHtml}
          `;
          personSelectInput.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 9999;
            padding: 12px;
            border: 2px solid #3b82f6;
            border-radius: 8px;
            font-size: 16px;
            background: white;
            box-shadow: 0 10px 30px rgba(0,0,0,0.3);
            min-width: 300px;
            max-width: 400px;
          `;
          
          const personBackdrop = document.createElement('div');
          personBackdrop.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            z-index: 9998;
          `;
          
          document.body.appendChild(personBackdrop);
          document.body.appendChild(personSelectInput);
          personSelectInput.focus();
          
          const personCleanup = () => {
            try {
              if (personBackdrop && personBackdrop.parentNode === document.body) {
                document.body.removeChild(personBackdrop);
              }
              if (personSelectInput && personSelectInput.parentNode === document.body) {
                document.body.removeChild(personSelectInput);
              }
            } catch (error) {
              console.warn('Person cleanup error (non-critical):', error);
            }
          };
          
          let isPersonProcessing = false;
          
          personSelectInput.addEventListener('change', async () => {
            if (isPersonProcessing) return;
            isPersonProcessing = true;
            
            await updateItemColumn(item, column, personSelectInput.value);
            personCleanup();
          });
          
          personBackdrop.addEventListener('click', () => {
            if (!isPersonProcessing) {
              personCleanup();
            }
          });
          
          personSelectInput.addEventListener('blur', () => {
            if (!isPersonProcessing) {
              setTimeout(personCleanup, 200);
            }
          });
          
          personSelectInput.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !isPersonProcessing) {
              personCleanup();
            }
          });
          
        } catch (error) {
          console.error('Erro ao buscar membros do quadro:', error);
          // Fallback to simple input
          const currentPersonValue = item.column_values?.[column.id] || '';
          const newPersonValue = prompt(`Atribuir pessoa para ${column.title}:\n\nErro ao carregar membros. Digite o ID da pessoa:`, currentPersonValue);
          
          if (newPersonValue !== null) {
            await updateItemColumn(item, column, newPersonValue.trim());
          }
        }
        break;
        
        case 'observacao': {
        const currentObs = item.column_values?.[column.id] || '';
        const newObs = prompt(`Observação para "${item.name}":`, currentObs);
        if (newObs !== null) await updateItemColumn(item, column, newObs);
        break;
      }

      default:
        // Check if it's a status column that might not have been handled above
        if (columnType.includes('status') || column.title?.toLowerCase().includes('status')) {
          // Treat as status column
          let statusOptions = [];
          if (column.options && Array.isArray(column.options)) {
            statusOptions = column.options;
          } else if (column.settings && column.settings.options) {
            statusOptions = column.settings.options;
          } else if (column.options && typeof column.options === 'string') {
            statusOptions = column.options.split(/[\\,|]/).filter(opt => opt.trim());
          } else {
            statusOptions = ['Pendente', 'Em Progresso', 'Concluído', 'Parado'];
          }
          
          const currentStatusValue = item.column_values?.[column.id] || '';
          
          let statusOptionsHtml = statusOptions.map((option) => 
            `<option value="${option}" ${option === currentStatusValue ? 'selected' : ''}>${option}</option>`
          ).join('');
          
          const statusSelectInput = document.createElement('select');
          statusSelectInput.innerHTML = `<option value="">Selecione um status</option>${statusOptionsHtml}`;
          statusSelectInput.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 9999;
            padding: 10px;
            border: 2px solid #3b82f6;
            border-radius: 8px;
            font-size: 16px;
            background: white;
            box-shadow: 0 10px 30px rgba(0,0,0,0.3);
            min-width: 200px;
          `;
          
          const statusBackdrop = document.createElement('div');
          statusBackdrop.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            z-index: 9998;
          `;
          
          document.body.appendChild(statusBackdrop);
          document.body.appendChild(statusSelectInput);
          statusSelectInput.focus();
          
          const statusCleanup = () => {
            try {
              if (statusBackdrop && statusBackdrop.parentNode === document.body) {
                document.body.removeChild(statusBackdrop);
              }
              if (statusSelectInput && statusSelectInput.parentNode === document.body) {
                document.body.removeChild(statusSelectInput);
              }
            } catch (error) {
              console.warn('Status cleanup error (non-critical):', error);
            }
          };
          
          let isStatusProcessing = false;
          
          statusSelectInput.addEventListener('change', async () => {
            if (isStatusProcessing) return;
            isStatusProcessing = true;
            
            await updateItemColumn(item, column, statusSelectInput.value);
            statusCleanup();
          });
          
          statusBackdrop.addEventListener('click', () => {
            if (!isStatusProcessing) {
              statusCleanup();
            }
          });
          
          statusSelectInput.addEventListener('blur', () => {
            if (!isStatusProcessing) {
              setTimeout(statusCleanup, 200);
            }
          });
          
          statusSelectInput.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !isStatusProcessing) {
              statusCleanup();
            }
          });
          
          return; // Exit the function after handling
        }
        
        toast.info(`Tipo de coluna "${column.type}" ainda não suportado para edição`);
    }
  };

  const updateItemColumn = async (item, column, newValue) => {
    try {
      const updatedColumnValues = {
        ...item.column_values,
        [column.id]: newValue
      };

      await api.put(`/projects/items/${item.id}`, {
        column_values: updatedColumnValues
      });

      // Verifica se o item é uma sub-tarefa (existe em subtasksData)
      const isSubtask = Object.values(subtasksData).some(
        subs => subs.some(s => s.id === item.id)
      );

      if (isSubtask) {
        // Atualiza subtasksData localmente sem recarregar o quadro inteiro
        setSubtasksData(prev => {
          const next = { ...prev };
          for (const parentId of Object.keys(next)) {
            const idx = next[parentId].findIndex(s => s.id === item.id);
            if (idx !== -1) {
              next[parentId] = [...next[parentId]];
              next[parentId][idx] = {
                ...next[parentId][idx],
                column_values: updatedColumnValues
              };
              break;
            }
          }
          return next;
        });
      } else {
        onDataChange();
      }

      toast.success('Coluna atualizada com sucesso!');
    } catch (error) {
      console.error('Error updating column:', error);
      toast.error('Erro ao atualizar coluna');
    }
  };

  const handleAddItemToGroup = (groupId) => {
    setNewTaskGroupId(groupId);
    setNewTaskName('');
    setShowNewTaskModal(true);
  };

  const handleAddNewTask = async () => {
    if (groups.length === 0) {
      toast.error('Crie um grupo antes de adicionar uma tarefa');
      return;
    }
    setNewTaskGroupId(groups[0]?.id || '');
    setNewTaskName('');
    setShowNewTaskModal(true);
  };

  const handleCreateTaskFromModal = async () => {
    if (!newTaskName.trim() || !newTaskGroupId) { toast.error('Informe o nome e o grupo da tarefa'); return; }
    await handleAddItem(newTaskGroupId, newTaskName.trim());
    setShowNewTaskModal(false);
  };

  const handleDragStart = (e, itemId) => {
    setDragItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, itemId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (itemId !== dragItemId) setDragOverItemId(itemId);
  };

  const handleDrop = async (e, targetItemId, groupId) => {
    e.preventDefault();
    if (!dragItemId || dragItemId === targetItemId) {
      setDragItemId(null);
      setDragOverItemId(null);
      return;
    }
    const groupItems = items
      .filter(i => i.group_id === groupId)
      .sort((a, b) => a.position - b.position);

    const dragIdx = groupItems.findIndex(i => i.id === dragItemId);
    const dropIdx = groupItems.findIndex(i => i.id === targetItemId);
    if (dragIdx === -1 || dropIdx === -1) return;

    const reordered = [...groupItems];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);

    try {
      await api.post('/projects/items/reorder', {
        item_ids: reordered.map(i => i.id),
        group_id: groupId
      });
      onDataChange();
    } catch (err) {
      toast.error('Erro ao reordenar itens');
    }
    setDragItemId(null);
    setDragOverItemId(null);
  };

  const handleDragEnd = () => {
    setDragItemId(null);
    setDragOverItemId(null);
  };

  // Drag-and-drop para sub-tarefas
  const handleSubDragStart = (e, subId) => {
    e.stopPropagation();
    setDragSubId(subId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSubDragOver = (e, subId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (subId !== dragSubId) setDragOverSubId(subId);
  };

  const handleSubDrop = async (e, targetSubId, parentId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragSubId || dragSubId === targetSubId) {
      setDragSubId(null);
      setDragOverSubId(null);
      return;
    }
    const subs = subtasksData[parentId] || [];
    const dragIdx = subs.findIndex(s => s.id === dragSubId);
    const dropIdx = subs.findIndex(s => s.id === targetSubId);
    if (dragIdx === -1 || dropIdx === -1) return;

    const reordered = [...subs];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);

    // Optimistic update local state immediately
    setSubtasksData(prev => ({ ...prev, [parentId]: reordered }));

    try {
      const parentItem = items.find(i => i.id === parentId);
      await api.post('/projects/items/reorder', {
        item_ids: reordered.map(s => s.id),
        group_id: parentItem?.group_id || reordered[0]?.group_id || ''
      });
    } catch {
      toast.error('Erro ao reordenar sub-tarefas');
      // Revert on error
      setSubtasksData(prev => ({ ...prev, [parentId]: subs }));
    }
    setDragSubId(null);
    setDragOverSubId(null);
  };

  // Reordenação vinda da lista (ProjectTaskList): recebe a nova ordem completa dos ids
  const handleReorderSubtasks = async (parentId, orderedIds) => {
    const subs = subtasksData[parentId] || [];
    const byId = new Map(subs.map(sub => [sub.id, sub]));
    const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean);
    if (reordered.length !== subs.length) return;
    setSubtasksData(prev => ({ ...prev, [parentId]: reordered }));
    try {
      const parentItem = items.find(i => i.id === parentId);
      await api.post('/projects/items/reorder', {
        item_ids: orderedIds,
        group_id: parentItem?.group_id || reordered[0]?.group_id || ''
      });
    } catch {
      toast.error('Erro ao reordenar sub-tarefas');
      setSubtasksData(prev => ({ ...prev, [parentId]: subs }));
    }
  };

  const handleSubDragEnd = () => {
    setDragSubId(null);
    setDragOverSubId(null);
  };

  const handleAddNewGroup = async () => {
    setNewGroupName('');
    setShowNewGroupModal(true);
  };

  const handleCreateGroupFromModal = async () => {
    if (!newGroupName.trim()) { toast.error('Informe o nome do grupo'); return; }
    
    try {
      await api.post('/projects/groups', {
        board_id: board.id,
        title: newGroupName.trim(),
        position: groups.length,
        color: `#${Math.floor(Math.random()*16777215).toString(16)}` // Random color
      });
      
      onDataChange();
      toast.success('Grupo criado com sucesso!');
      setShowNewGroupModal(false);
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Erro ao criar grupo');
    }
  };

  const renderCustomColumnValue = (item, column) => {
    const value = item.column_values?.[column.id];
    const columnType = (column.type || '').toLowerCase();
    
    // Handle null/undefined values first
    // time_tracking always shows Start button even with no accumulated time
    // progress always shows bar if sub-tasks are loaded
    const isSpecialType = columnType === 'time_tracking' || columnType === 'progress' || columnType === 'progresso';
    if ((value === null || value === undefined) && !isSpecialType) {
      return <span className="text-gray-400 italic">—</span>;
    }
    
    // Handle object values (fix for {object Object})
    if (typeof value === 'object' && value !== null) {
      // If it's an object, try to extract meaningful value
      if (value.value !== undefined) {
        return renderCustomColumnValue(item, { ...column, id: column.id }, value.value);
      } else if (value.text !== undefined) {
        return <span className="text-gray-900">{value.text}</span>;
      } else {
        // Last resort: stringify the object
        return <span className="text-red-500 text-xs">{JSON.stringify(value)}</span>;
      }
    }

    switch (columnType) {
      case 'text':
      case 'texto':
        return <span className="text-gray-900">{String(value)}</span>;

      case 'number':
      case 'numero':
        const numValue = parseFloat(value);
        return <span className="text-gray-900 font-mono">{isNaN(numValue) ? value : numValue}</span>;

      case 'checkbox':
      case 'check':
        const isChecked = Boolean(value);
        return (
          <div className="flex items-center">
            {isChecked ? (
              <span className="w-4 h-4 bg-green-500 rounded text-white flex items-center justify-center text-xs">✓</span>
            ) : (
              <span className="w-4 h-4 border border-gray-300 rounded"></span>
            )}
          </div>
        );

      case 'progress':
      case 'progresso': {
        // If item has sub-tasks loaded, show segmented bar by status
        const subs = subtasksData[item.id];
        if (subs && subs.length > 0) {
          // Define status → color mapping
          const STATUS_COLORS = {
            feito: '#22c55e', done: '#22c55e', concluido: '#22c55e', concluído: '#22c55e', completed: '#22c55e',
            homologacao: '#eab308', homologação: '#eab308', em_revisao: '#eab308', review: '#eab308',
            pendente: '#eab308', aguardando: '#eab308', waiting: '#eab308',
            parado: '#ef4444', stuck: '#ef4444', bloqueado: '#ef4444',
            em_progresso: '#3b82f6', working_on_it: '#3b82f6', 'in_progress': '#3b82f6',
            to_do: '#94a3b8', novo: '#94a3b8',
          };

          const DEFAULT_COLOR = '#94a3b8';
          // Count by status
          const counts = {};
          subs.forEach(s => {
            const st = (s.column_values?.status || s.status || 'to_do').toLowerCase().replace(/\s+/g,'_');
            counts[st] = (counts[st] || 0) + 1;
          });
          const total = subs.length;
          const segments = Object.entries(counts).map(([st, count]) => ({
            st, count, pct: (count / total) * 100,
            color: STATUS_COLORS[st] || DEFAULT_COLOR,
          }));
          // Calculate overall % (done / total)
          const doneStates = ['feito','done','concluido','concluído','completed'];
          const doneCount = subs.filter(s => doneStates.includes((s.column_values?.status || s.status || '').toLowerCase())).length;
          const overallPct = Math.round((doneCount / total) * 100);
          return (
            <div className="w-full space-y-1">
              <div className="flex h-3 rounded-full overflow-hidden w-full bg-gray-100">
                {segments.map(seg => (
                  <div key={seg.st} title={`${seg.st}: ${seg.count} (${Math.round(seg.pct)}%)`}
                    style={{ width: `${seg.pct}%`, backgroundColor: seg.color }} />
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{total} sub-tarefas</span>
                <span className="text-xs font-medium text-gray-700">{overallPct}% concluído</span>
              </div>
            </div>
          );
        }
        // Fallback: simple manual progress bar
        const progressValue = parseInt(value) || 0;
        return (
          <div className="w-full space-y-0.5">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.max(0, Math.min(100, progressValue))}%` }} />
            </div>
            <span className="text-xs text-gray-500">{progressValue}%</span>
          </div>
        );
      }

      case 'date':
      case 'data':
        if (value) {
          try {
            return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
              {new Date(value).toLocaleDateString('pt-BR')}
            </span>;
          } catch (e) {
            return <span className="text-red-500 text-xs">Data inválida</span>;
          }
        }
        return <span className="text-gray-400 italic">—</span>;

      case 'status':
        // NEW: Support for status columns
        if (value) {
          // Get status options from column settings FIRST, then options as fallback
          let statusOptions = [];
          if (column.settings && column.settings.options && Array.isArray(column.settings.options)) {
            statusOptions = column.settings.options;
          } else if (column.options && Array.isArray(column.options)) {
            statusOptions = column.options;
          } else if (column.options && typeof column.options === 'string') {
            statusOptions = column.options.split(/[\\,|]/).filter(opt => opt.trim());
          } else {
            // Default status options if none defined
            statusOptions = ['Pendente', 'Em Progresso', 'Concluído', 'Parado'];
          }
          
          const currentStatusValue = String(value); // Force string conversion
          
          console.log(`✅ Status: "${column.title}" - Valor: "${currentStatusValue}"`);
          
          // Define colors for different status values
          const getStatusColor = (statusValue) => {
            const lowerValue = statusValue.toLowerCase();
            if (lowerValue.includes('conclu') || lowerValue.includes('feito') || lowerValue.includes('done') || lowerValue.includes('disponível')) {
              return 'bg-green-100 text-green-800';
            } else if (lowerValue.includes('progres') || lowerValue.includes('andamento') || lowerValue.includes('working')) {
              return 'bg-blue-100 text-blue-800';
            } else if (lowerValue.includes('parado') || lowerValue.includes('stuck') || lowerValue.includes('bloqueado') || lowerValue.includes('esgotado')) {
              return 'bg-red-100 text-red-800';
            } else if (lowerValue.includes('pendente') || lowerValue.includes('waiting') || lowerValue.includes('review') || lowerValue.includes('reposição')) {
              return 'bg-yellow-100 text-yellow-800';
            } else {
              return 'bg-gray-100 text-gray-800';
            }
          };
          
          return <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(currentStatusValue)}`}>
            {currentStatusValue}
          </span>;
        }
        return <span className="text-gray-400 italic">—</span>;

      case 'dropdown':
      case 'lista_suspensa':
      case 'select':
        if (value) {
          // Get real options for validation from settings FIRST, then column.options as fallback
          let realOptions = [];
          if (column.settings && column.settings.options && Array.isArray(column.settings.options)) {
            realOptions = column.settings.options;
          } else if (column.options && Array.isArray(column.options)) {
            realOptions = column.options;
          } else if (column.options && typeof column.options === 'string') {
            // Parse string options like "BAD\\GOOD" or "option1,option2,option3"
            realOptions = column.options.split(/[\\,|]/).filter(opt => opt.trim());
          } else {
            // Fallback only if no options are defined
            realOptions = ['Opção 1', 'Opção 2', 'Opção 3'];
          }
          
          const currentDropdownValue = String(value); // Force string conversion
          
          console.log(`✅ Dropdown: "${column.title}" - Valor: "${currentDropdownValue}"`);
          
          // Show the actual value, but validate against real options
          const isValidOption = realOptions.length === 0 || realOptions.includes(currentDropdownValue);
          const displayValue = isValidOption ? currentDropdownValue : `${currentDropdownValue} (custom)`;
          
          return <span className={`px-2 py-1 rounded text-xs font-medium ${
            isValidOption ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {displayValue}
          </span>;
        }
        return <span className="text-gray-400 italic">—</span>;

      case 'person':
        // Handle person/member assignment columns - show real member names when possible
        if (value) {
          console.log(`✅ Person: "${column.title}" - Valor: "${value}"`);

          // Try to get board ID to fetch real member name
          const currentBoardId = window.location.pathname.includes('/projects') ? 
            new URLSearchParams(window.location.search).get('board') || 
            // Try to get board ID from the items data
            (items && items.length > 0 ? items[0].board_id : null) : null;

          // For now, show a simplified version with member ID
          // TODO: Cache board members in component state for better performance
          return (
            <div className="flex items-center space-x-2">
              <span className="text-lg">👤</span>
              <span className="font-medium text-sm text-gray-700">
                {String(value).includes('@') ? String(value).split('@')[0] : `Membro`}
              </span>
            </div>
          );
        }
        return <span className="text-gray-400 italic">—</span>;

      case 'formula': {
        const formulaStr = column.settings?.formula || '';
        const result = computeFormula(formulaStr, item.column_values, customColumns);
        const isErr = result === '#ERRO';
        return (
          <span className={`font-mono text-sm ${isErr ? 'text-red-500' : 'text-gray-800'}`}>
            {isErr ? '#ERRO' : String(result)}
          </span>
        );
      }

      case 'file': {
        const fileIds = Array.isArray(value) ? value : [];
        return (
          <div className="flex items-center space-x-1">
            <span className="text-sm">📎</span>
            {fileIds.length > 0 ? (
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                {fileIds.length}
              </span>
            ) : (
              <span className="text-xs text-gray-400">Anexar</span>
            )}
          </div>
        );
      }

      case 'rating':
        const ratingVal = parseInt(value) || 0;
        return (
          <div className="flex items-center">
            {[1,2,3,4,5].map(s => (
              <span key={s} className={`text-sm ${s <= ratingVal ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
            ))}
          </div>
        );

      case 'vote':
        const voteVal = parseInt(value) || 0;
        return (
          <div className="flex items-center space-x-1">
            <span className="text-sm">👍</span>
            <span className="text-sm font-medium text-gray-700">{voteVal}</span>
          </div>
        );

      case 'time_tracking': {
        const timerKey = `${item.id}_${column.id}`;
        const isRunning = Boolean(activeTimers[timerKey]);
        const { minutes: storedMins } = parseTimerValue(value);
        let displayMins;
        if (isRunning) {
          const elapsed = Math.max(0, Math.floor((Date.now() - activeTimers[timerKey]) / 60000));
          displayMins = storedMins + elapsed;
        } else if (stoppedTimerValues[timerKey] !== undefined) {
          // Stopping in-flight: show computed final value until DB refresh arrives
          displayMins = stoppedTimerValues[timerKey];
        } else {
          displayMins = storedMins;
        }
        // Check if this is a parent with sub-tasks that have time_tracking
        const subs = subtasksData[item.id];
        const hasSubTime = subs && subs.length > 0 && subs.some(s => s.column_values?.[column.id]);
        let subTotal = 0;
        if (hasSubTime) {
          subs.forEach(s => {
            const subKey = `${s.id}_${column.id}`;
            let subMins;
            if (stoppedTimerValues[subKey] !== undefined) {
              subMins = stoppedTimerValues[subKey];
            } else {
              const { minutes: rawMins } = parseTimerValue(s.column_values?.[column.id]);
              const subElapsed = activeTimers[subKey]
                ? Math.max(0, Math.floor((Date.now() - activeTimers[subKey]) / 60000))
                : 0;
              subMins = rawMins + subElapsed;
            }
            subTotal += subMins;
          });
        }
        // Guard against negative display (clock drift between client and server)
        const safeDisplay = Math.max(0, displayMins);
        const dispH = Math.floor(safeDisplay / 60);
        const dispM = safeDisplay % 60;
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`text-xs font-mono px-2 py-0.5 rounded ${isRunning ? 'bg-green-100 text-green-800 animate-pulse' : 'bg-gray-100 text-gray-800'}`}>
              {dispH}h {dispM.toString().padStart(2,'0')}m
            </span>
            <button
              onClick={e => { e.stopPropagation(); handleTimerToggle(item, column); }}
              title={isRunning ? 'Parar cronômetro' : 'Iniciar cronômetro'}
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors ${isRunning ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
              {isRunning ? '⏹' : '▶'}
            </button>
            {hasSubTime && (
              <span title={`Soma das sub-tarefas: ${Math.floor(subTotal/60)}h ${(subTotal%60).toString().padStart(2,'0')}m`}
                className="text-xs text-blue-600 cursor-help">Σ{Math.floor(subTotal/60)}h{(subTotal%60).toString().padStart(2,'0')}</span>
            )}
          </div>
        );
      }
      case 'observacao':
        return value
          ? <span className="text-gray-600 text-xs italic truncate">{String(value)}</span>
          : <span className="text-gray-300 text-xs italic">—</span>;


      default:
        // Fallback for unknown types or string values
        return <span className="text-gray-900">{String(value)}</span>;
    }
  };

  // Timer: start/stop for time_tracking columns — persisted in backend, synced via SSE
  const handleTimerToggle = async (item, column) => {
    const timerKey = `${item.id}_${column.id}`;
    if (activeTimers[timerKey]) {
      // Compute final value optimistically so display doesn't flash to old stored value
      const elapsed = Math.max(0, Math.floor((Date.now() - activeTimers[timerKey]) / 60000));
      const { minutes: currentStored } = parseTimerValue(item.column_values?.[column.id]);
      const finalMins = currentStored + elapsed;
      setStoppedTimerValues(prev => ({ ...prev, [timerKey]: finalMins }));
      // Remove from active timers so the tick button becomes "play"
      setActiveTimers(prev => { const next = { ...prev }; delete next[timerKey]; return next; });
      try {
        const res = await api.post(`/projects/items/${item.id}/timer/stop`, { column_id: column.id });
        const { total_minutes: total, elapsed_minutes: elapsedServer } = res.data;
        // Wait for data to refresh before clearing the optimistic value
        if (onDataChange) await onDataChange();
        toast.success(`Tempo registrado: +${elapsedServer}min (total ${Math.floor(total/60)}h${(total%60).toString().padStart(2,'0')}m)`);
      } catch (err) {
        toast.error('Erro ao parar cronômetro');
      } finally {
        setStoppedTimerValues(prev => { const n = { ...prev }; delete n[timerKey]; return n; });
      }
    } else {
      // Start: backend stores timer_started_at
      try {
        const res = await api.post(`/projects/items/${item.id}/timer/start`, { column_id: column.id });
        setActiveTimers(prev => ({ ...prev, [timerKey]: new Date(res.data.timer_started_at).getTime() }));
        toast.info('Cronômetro iniciado ▶');
      } catch (err) {
        toast.error('Erro ao iniciar cronômetro');
      }
    }
  };

  const getMemberInfo = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member || { name: 'Usuário', avatar: null };
  };

  const getAssignedMembers = (item) => {
    const assignedIds = item.column_values?.assigned_members || [];
    if (!Array.isArray(assignedIds)) return [];
    return assignedIds.map(id => getMemberInfo(id));
  };

  // ── File upload handler ────────────────────────────────────────────────────
  const handleFileSelected = async (e) => {
    const fileObj = e.target.files?.[0];
    if (!fileObj || !pendingFileUpload) return;

    const { item, column } = pendingFileUpload;
    const formData = new FormData();
    formData.append('file', fileObj);

    try {
      toast.loading('Enviando arquivo...', { id: 'file-upload' });
      const response = await api.post(`/projects/items/${item.id}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileId = response.data.id;
      const existingIds = Array.isArray(item.column_values?.[column.id])
        ? item.column_values[column.id]
        : [];
      await updateItemColumn(item, column, [...existingIds, fileId]);

      toast.success(`"${response.data.original_name}" enviado com sucesso!`, { id: 'file-upload' });
    } catch (err) {
      toast.error('Erro ao enviar arquivo', { id: 'file-upload' });
    } finally {
      setPendingFileUpload(null);
      e.target.value = '';
    }
  };

  return (
    <div ref={tableRef} className="w-full">
      <ProjectTaskList
        board={board}
        groupedItems={groupedItems}
        subtasksData={subtasksData}
        boardListSummary={boardListSummary}
        advancedFilter={advancedFilter}
        members={members}
        expandedSubtasks={expandedSubtasks}
        onToggleSubtasks={toggleSubtasks}
        onOpenTask={handleItemClick}
        onAddTask={handleAddNewTask}
        onAddTaskToGroup={handleAddItemToGroup}
        onAddGroup={handleAddNewGroup}
        onAddSubtask={openNewSubtaskModal}
        onReorderSubtasks={handleReorderSubtasks}
        customColumns={customColumns}
        onManageFields={onManageFields}
        canDeleteItems={canDeleteItems}
        onDeleteTask={handleDeleteItem}
        canManageGroups={canManageGroups}
        onDeleteGroup={group => setDeletingGroup({ id: group.id, title: group.title })}
        onTimerToggle={handleTimerToggle}
        isTimerRunning={isTimerRunning}
        getTimerDisplayMinutes={getTimerDisplayMinutes}
        statusOptions={boardStatusKeys}
        statusConfig={boardStatuses}
        onManageStatuses={() => setShowStatusManager(true)}
        onStatusChange={handleStatusChange}
        priorityOptions={priorityOptions}
        onPriorityChange={handlePriorityChange}
        onFieldChange={handleInlineFieldChange}
        onAssignMembers={handleAssignMember}
        onlyMyTasks={onlyMyTasks}
        onToggleOnlyMyTasks={onToggleOnlyMyTasks}
        onClearAdvancedFilters={onClearAdvancedFilters}
        darkMode={darkMode}
      />
      <div className="hidden">
      {/* Hidden file input for file column uploads */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
      {/* Header da tabela */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{board?.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{items.length} itens</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => handleAddNewTask()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              + Nova Tarefa
            </Button>
            <Button
              variant="outline"
              onClick={() => handleAddNewGroup()}
              size="sm"
            >
              + Novo Grupo
            </Button>
          </div>
        </div>
      </div>

      {/* Container com scroll horizontal */}
      <div className="overflow-x-auto">
        {/* Cabeçalho das colunas - estilo Monday.com com grid dinâmico */}
        <div className="bg-gray-50 border-b border-gray-300">
          <div 
            className="divide-x divide-gray-200 text-sm font-semibold text-gray-700 uppercase tracking-wide"
            style={{ display: 'grid', gridTemplateColumns: getDynamicGridCols(), minWidth: 'fit-content' }}
          >
          {colOrder.map(colKey => {
            const colLabels = { name: 'Elemento', status: 'Status', person: 'Pessoa', date: 'Data', priority: 'Prioridade' };
            return (
              <div
                key={colKey}
                draggable
                onDragStart={(e) => handleColDragStart(e, colKey)}
                onDragOver={(e) => handleColDragOver(e, colKey)}
                onDrop={(e) => handleColDrop(e, colKey)}
                onDragEnd={handleColDragEnd}
                className={`px-4 py-3 border-r border-gray-200 flex items-center relative select-none cursor-grab active:cursor-grabbing transition-colors ${
                  dragOverCol === colKey ? 'bg-blue-50' : ''
                } ${draggingCol === colKey ? 'opacity-40' : ''}`}
                style={{ overflow: 'visible' }}
              >
                <svg className="w-3 h-3 mr-1.5 text-gray-300 flex-shrink-0" viewBox="0 0 10 16" fill="currentColor">
                  <circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/>
                  <circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/>
                  <circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/>
                </svg>
                <span className="truncate flex-1">{colLabels[colKey]}</span>
                <div
                  className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400 transition-colors z-10"
                  onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(colKey, e); }}
                  title="Arrastar para redimensionar"
                />
              </div>
            );
          })}
          
          {customColumns && customColumns.length > 0 && customColumns.map((column, index) => (
            <div key={`header-${column.id}-${index}`} className="px-4 py-3 border-r border-gray-200 flex items-center relative">
              <span title={column.title || 'Coluna Personalizada'} className="truncate flex-1">
                {(column.title || column.name || 'Coluna').toUpperCase()}
              </span>
            </div>
          ))}
          
          <div className="px-4 py-3 flex items-center justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleAddNewGroup()}
              className="text-gray-400 hover:text-blue-600 h-6 w-6 p-0 text-lg"
              title="Adicionar Grupo"
            >
              +
            </Button>
          </div>
          </div>
        </div>
      </div>

      {/* Conteúdo da tabela */}
      <div className="space-y-6">
        {groupedItems.map((group, groupIndex) => {
          const groupColor = getGroupColor(groupIndex);
          
          return (
            <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Header do grupo */}
              <div 
                className={`${groupColor.bg} px-4 py-3 border-l-4 rounded-t-lg`}
                style={{ borderLeftColor: groupColor.border }}
              >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-xl">{groupColor.icon}</span>
                  {editingGroupId === group.id ? (
                    <input
                      autoFocus
                      value={editingGroupTitle}
                      onChange={e => setEditingGroupTitle(e.target.value)}
                      onBlur={() => handleRenameGroup(group.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRenameGroup(group.id);
                        if (e.key === 'Escape') setEditingGroupId(null);
                      }}
                      onClick={e => e.stopPropagation()}
                      className={`font-semibold ${groupColor.text} bg-white bg-opacity-70 border border-current rounded px-2 py-0.5 focus:outline-none w-44`}
                    />
                  ) : (
                    <span
                      className={`font-semibold ${groupColor.text} cursor-pointer hover:underline`}
                      onDoubleClick={e => { e.stopPropagation(); setEditingGroupId(group.id); setEditingGroupTitle(group.title); }}
                      title="Duplo clique para renomear"
                    >
                      {group.title}
                    </span>
                  )}
                  <Badge variant="secondary" className={`${groupColor.bg} ${groupColor.text} border-0`}>
                    {group.items?.length || 0} items
                  </Badge>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddItemToGroup(group.id)}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 h-8 px-3"
                    title="Adicionar item a este grupo"
                  >
                    + Item
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingGroup({ id: group.id, title: group.title })}
                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                    title="Excluir grupo"
                  >
                    🗑️
                  </Button>
                </div>
              </div>
              </div>

              {/* Itens do grupo */}
              <div className="divide-y divide-gray-100">
                {group.items?.map((item) => {
                  const statusStyle = getStatusStyle(item.column_values?.status);
                  const assignedMembers = getAssignedMembers(item);
                  
                  return (
                    <React.Fragment key={item.id}>
                    <div 
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.id)}
                      onDragOver={(e) => handleDragOver(e, item.id)}
                      onDrop={(e) => handleDrop(e, item.id, group.id)}
                      onDragEnd={handleDragEnd}
                      className={`group divide-x divide-gray-200 cursor-pointer transition-colors border-b border-gray-100 ${
                        dragOverItemId === item.id ? 'bg-blue-50 border-l-2 border-l-blue-400' : 'hover:bg-gray-50'
                      } ${dragItemId === item.id ? 'opacity-50' : ''}`}
                      style={{ 
                        display: 'grid', 
                        gridTemplateColumns: getDynamicGridCols(),
                        minWidth: 'fit-content'
                      }}
                      onClick={() => handleItemClick(item)}
                    >
                      {/* Células das colunas em ordem dinâmica (colOrder) */}
                      {colOrder.map(colKey => {
                        if (colKey === 'name') return (
                          <div key="name" className="px-4 py-3 border-r border-gray-200 flex items-center">
                            <div
                              className="mr-2 cursor-grab active:cursor-grabbing flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors select-none"
                              title="Arrastar para reordenar"
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                                <circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/>
                                <circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/>
                                <circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/>
                              </svg>
                            </div>
                            <button
                              className="mr-1.5 flex-shrink-0 text-gray-400 hover:text-blue-500 transition-colors"
                              title={expandedSubtasks[item.id] ? 'Recolher sub-tarefas' : 'Expandir sub-tarefas'}
                              onClick={e => { e.stopPropagation(); toggleSubtasks(item.id); }}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                                {expandedSubtasks[item.id] ? <path d="M2 4l4 4 4-4"/> : <path d="M4 2l4 4-4 4"/>}
                              </svg>
                            </button>
                            <div className ="flex flex-col min-w-0 flex-1">
                            <span className="text-gray-900 font-medium truncate flex-1">{item.name}</span>
                            {(item.notes || item.description) && (
                              <span className="text-xs text-gray-400 truncate">{item.notes || item.description}</span>
                            )}
                            </div>
                            <button
                              className="ml-1 flex-shrink-0 text-gray-300 hover:text-green-500 transition-colors opacity-0 group-hover:opacity-100"
                              title="Adicionar sub-tarefa"
                              onClick={e => { e.stopPropagation(); setAddingSubtask(item.id); setExpandedSubtasks(prev => ({...prev, [item.id]: true})); }}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 1v10M1 6h10"/>
                              </svg>
                            </button>
                          </div>
                        );
                        if (colKey === 'status') return (
                          <div key="status" className="px-2 py-2 border-r border-gray-200 flex items-center justify-center relative">
                            {editingCell?.itemId === item.id && editingCell?.field === 'status' ? (
                              <div className="absolute z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-1 min-w-[160px]" style={{ top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
                                <input
                                  autoFocus
                                  value={creatableInput}
                                  onChange={e => setCreatableInput(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && creatableInput.trim()) {
                                      const val = creatableInput.trim().toLowerCase().replace(/\s+/g, '_');
                                      if (!statusOptions.includes(val)) setStatusOptions(prev => [...prev, val]);
                                      saveInlineEdit(item.id, 'status', val);
                                    }
                                    if (e.key === 'Escape') { setEditingCell(null); }
                                  }}
                                  placeholder="Buscar ou criar..."
                                  className="w-full px-2 py-1 text-xs border-b border-gray-100 mb-1 focus:outline-none"
                                />
                                <div className="max-h-40 overflow-y-auto">
                                  {statusOptions.filter(s => !creatableInput || s.includes(creatableInput.toLowerCase())).map(s => (
                                    <button key={s} onClick={() => saveInlineEdit(item.id, 'status', s)}
                                      className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 hover:text-blue-700">
                                      {s.replace(/_/g, ' ')}
                                    </button>
                                  ))}
                                  {creatableInput && !statusOptions.includes(creatableInput.toLowerCase().replace(/\s+/g,'_')) && (
                                    <button onClick={() => {
                                      const val = creatableInput.trim().toLowerCase().replace(/\s+/g,'_');
                                      setStatusOptions(prev => [...prev, val]);
                                      saveInlineEdit(item.id, 'status', val);
                                    }} className="w-full text-left px-2 py-1.5 text-xs rounded bg-green-50 text-green-700 font-medium">
                                      + Criar "{creatableInput}"
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : null}
                            <div
                              onClick={e => openInlineEdit(e, item.id, 'status', item.column_values?.status)}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 min-w-[90px] justify-center ${statusStyle.bg} ${statusStyle.text}`}
                              title="Clique para editar"
                            >
                              {statusStyle.label}
                            </div>
                          </div>
                        );
                        if (colKey === 'person') return (
                          <div key="person" className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                            {(() => {
                              const assignedPersons = item.column_values?.persons || [];
                              const assignedPerson = item.column_values?.person || item.column_values?.person_id;
                              const responsibleList = assignedPersons.length > 0
                                ? assignedPersons
                                : (assignedPerson && assignedPerson !== '' && assignedPerson !== 'undefined')
                                  ? [{ name: assignedPerson, id: item.column_values?.person_id || assignedPerson }]
                                  : [];
                              return responsibleList.length > 0 ? (
                                <div className="flex items-center space-x-1">
                                  <div className="flex -space-x-1">
                                    {responsibleList.slice(0, 2).map((person, index) => (
                                      <div key={person.id || `p${index}`}
                                        className={`w-7 h-7 ${avatarColor(person.name, person.id)} rounded-full border-2 border-white flex items-center justify-center text-[10px] text-white font-bold cursor-pointer hover:ring-2 hover:ring-blue-300`}
                                        title={`${displayName(person.name, person.id)} — clique para gerenciar`}
                                        onClick={(e) => { e.stopPropagation(); handleAssignMember(item.id); }}>
                                        {avatarInitials(person.name, person.id)}
                                      </div>
                                    ))}
                                    {responsibleList.length > 2 && (
                                      <div className="w-7 h-7 bg-gray-400 rounded-full border-2 border-white flex items-center justify-center text-xs text-white font-medium cursor-pointer"
                                        onClick={(e) => { e.stopPropagation(); handleAssignMember(item.id); }}>
                                        +{responsibleList.length - 2}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-xs text-white cursor-pointer hover:bg-green-600 transition-colors" onClick={(e) => { e.stopPropagation(); handleAssignMember(item.id); }} title="Adicionar mais responsáveis">+</div>
                                </div>
                              ) : (
                                <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-500 cursor-pointer hover:bg-blue-200 transition-colors" onClick={(e) => { e.stopPropagation(); handleAssignMember(item.id); }} title="Atribuir responsável">+</div>
                              );
                            })()}
                          </div>
                        );
                        if (colKey === 'date') return (
                          <div key="date" className="px-3 py-2 border-r border-gray-200 flex items-center">
                            {editingCell?.itemId === item.id && editingCell?.field === 'due_date' ? (
                              <input
                                type="date"
                                autoFocus
                                value={editingValue}
                                onChange={e => setEditingValue(e.target.value)}
                                onBlur={e => { if (e.target.value) saveInlineEdit(item.id, 'due_date', e.target.value); else setEditingCell(null); }}
                                onKeyDown={e => { if (e.key === 'Enter') saveInlineEdit(item.id, 'due_date', editingValue); if (e.key === 'Escape') setEditingCell(null); }}
                                onClick={e => e.stopPropagation()}
                                className="border border-blue-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 w-full"
                              />
                            ) : (
                              <span
                                onClick={e => openInlineEdit(e, item.id, 'due_date', item.column_values?.due_date || '')}
                                title="Clique para editar"
                                className={`cursor-pointer rounded px-2 py-1 text-xs font-medium hover:ring-1 hover:ring-blue-300 ${item.column_values?.due_date ? 'bg-green-100 text-green-800' : 'text-gray-400'}`}
                              >
                                {item.column_values?.due_date ? new Date(item.column_values.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '+ Data'}
                              </span>
                            )}
                          </div>
                        );
                        if (colKey === 'priority') return (
                          <div key="priority" className="px-2 py-2 border-r border-gray-200 flex items-center relative">
                            {editingCell?.itemId === item.id && editingCell?.field === 'priority' ? (
                              <div className="absolute z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-1 min-w-[150px]" style={{ top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
                                <input
                                  autoFocus
                                  value={creatableInput}
                                  onChange={e => setCreatableInput(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && creatableInput.trim()) {
                                      const val = creatableInput.trim().toLowerCase().replace(/\s+/g,'_');
                                      if (!priorityOptions.includes(val)) setPriorityOptions(prev => [...prev, val]);
                                      saveInlineEdit(item.id, 'priority', val);
                                    }
                                    if (e.key === 'Escape') setEditingCell(null);
                                  }}
                                  placeholder="Buscar ou criar..."
                                  className="w-full px-2 py-1 text-xs border-b border-gray-100 mb-1 focus:outline-none"
                                />
                                <div className="max-h-36 overflow-y-auto">
                                  {priorityOptions.filter(p => !creatableInput || p.includes(creatableInput.toLowerCase())).map(p => (
                                    <button key={p} onClick={() => saveInlineEdit(item.id, 'priority', p)} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 hover:text-blue-700">
                                      {p === 'alta' ? '🔴' : p === 'media' ? '🟡' : p === 'baixa' ? '🟢' : p === 'critica' ? '🚨' : '⚪'} {p.replace(/_/g,' ')}
                                    </button>
                                  ))}
                                  {creatableInput && !priorityOptions.includes(creatableInput.toLowerCase().replace(/\s+/g,'_')) && (
                                    <button onClick={() => {
                                      const val = creatableInput.trim().toLowerCase().replace(/\s+/g,'_');
                                      setPriorityOptions(prev => [...prev, val]);
                                      saveInlineEdit(item.id, 'priority', val);
                                    }} className="w-full text-left px-2 py-1.5 text-xs rounded bg-green-50 text-green-700 font-medium">
                                      + Criar "{creatableInput}"
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : null}
                            <div
                              onClick={e => openInlineEdit(e, item.id, 'priority', item.column_values?.priority)}
                              title="Clique para editar"
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium cursor-pointer hover:opacity-80 ${
                                item.column_values?.priority === 'alta' || item.column_values?.priority === 'critica' ? 'bg-red-100 text-red-800' :
                                item.column_values?.priority === 'media' ? 'bg-yellow-100 text-yellow-800' :
                                item.column_values?.priority === 'baixa' ? 'bg-green-100 text-green-800' :
                                'text-gray-400'
                              }`}
                            >
                              {item.column_values?.priority
                                ? `${item.column_values.priority === 'alta' ? '🔴' : item.column_values.priority === 'media' ? '🟡' : item.column_values.priority === 'baixa' ? '🟢' : item.column_values.priority === 'critica' ? '🚨' : '⚪'} ${item.column_values.priority.replace(/_/g,' ')}`
                                : '+ Prioridade'
                              }
                            </div>
                          </div>
                        );
                        return null;
                      })}

                      {/* Colunas personalizadas - Uma célula para cada coluna */}
                      {customColumns && customColumns.length > 0 ? customColumns.map((column, index) => (
                        <div 
                          key={`cell-${column.id}-${item.id}-${index}`} 
                          className="px-4 py-3 border-r border-gray-200 flex items-center cursor-pointer hover:bg-blue-50"
                          title={`${column.title}: ${renderCustomColumnValue(item, column) || 'Vazio'}`}
                          onClick={(e) => handleCustomColumnClick(e, item, column)}
                        >
                          <div className="text-sm truncate w-full">
                            {renderCustomColumnValue(item, column) || '-'}
                          </div>
                        </div>
                      )) : null}

                      {/* Ações */}
                      <div className="px-2 py-3 flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-gray-600 h-6 w-6 p-0"
                          title="Mais opções"
                        >
                          ⋯
                        </Button>
                      </div>
                    </div>

                    {/* Sub-tarefas expandidas */}
                    {expandedSubtasks[item.id] && (
                      <div className="bg-gray-50 border-t border-gray-100">
                        {(subtasksData[item.id] || []).map((sub) => {
                          const subStatusStyle = getStatusStyle(sub.column_values?.status);
                          return (
                          <div
                            key={sub.id}
                            draggable
                            onDragStart={e => handleSubDragStart(e, sub.id)}
                            onDragOver={e => handleSubDragOver(e, sub.id)}
                            onDrop={e => handleSubDrop(e, sub.id, item.id)}
                            onDragEnd={handleSubDragEnd}
                            className={`divide-x divide-gray-200 border-b border-gray-100 transition-colors ${
                              dragOverSubId === sub.id ? 'bg-blue-100 border-l-2 border-l-blue-400' : 'bg-gray-50 hover:bg-blue-50'
                            } ${dragSubId === sub.id ? 'opacity-40' : ''}`}
                            style={{ display: 'grid', gridTemplateColumns: getDynamicGridCols(), minWidth: 'fit-content' }}
                          >
                            {/* Nome da sub-tarefa */}
                            <div className="px-4 py-2 border-r border-gray-200 flex items-center">
                              {/* Drag handle */}
                              <div
                                className="mr-1 ml-4 cursor-grab active:cursor-grabbing flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors select-none"
                                title="Arrastar para reordenar"
                                onMouseDown={e => e.stopPropagation()}
                              >
                                <svg width="8" height="14" viewBox="0 0 10 16" fill="currentColor">
                                  <circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/>
                                  <circle cx="2" cy="7" r="1.5"/><circle cx="8" cy="7" r="1.5"/>
                                  <circle cx="2" cy="12" r="1.5"/><circle cx="8" cy="12" r="1.5"/>
                                </svg>
                              </div>
                              <span className="text-gray-300 text-xs mr-2 shrink-0">↳</span>
                              <div className="flex flex-col min-w-0 flex-1">
                                <div className="flex items-center gap-1">
                                 <span
                                  className="text-gray-700 text-sm truncate cursor-pointer hover:text-blue-600 hover:underline"
                                  title="Clique para abrir detalhes"
                                  onClick={() => handleItemClick(sub)}
                                >{sub.name}</span>
                            </div>
                            {(sub.notes || sub.description) && (
                              <span className="text-xs text-gray-400 truncate">{sub.notes || sub.description}</span>
                            )}
                          </div>
                          </div>

                            {/* Colunas dinâmicas (respeita colOrder igual ao item pai) */}
                            {colOrder.filter(k => k !== 'name').map(colKey => {
                              if (colKey === 'status') return (
                                <div key={`sub-status-${sub.id}`} className="px-2 py-2 border-r border-gray-200 flex items-center justify-center relative">
                                  {editingCell?.itemId === sub.id && editingCell?.field === 'status' && (
                                    <div className="absolute z-30 bg-white border border-gray-200 rounded-xl shadow-lg p-1 min-w-[160px]" style={{ top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
                                      <input autoFocus value={creatableInput} onChange={e => setCreatableInput(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter' && creatableInput.trim()) { const val = creatableInput.trim().toLowerCase().replace(/\s+/g, '_'); if (!statusOptions.includes(val)) setStatusOptions(prev => [...prev, val]); saveInlineEdit(sub.id, 'status', val); }
                                          if (e.key === 'Escape') setEditingCell(null);
                                        }}
                                        placeholder="Buscar ou criar..." className="w-full px-2 py-1 text-xs border-b border-gray-100 mb-1 focus:outline-none" />
                                      <div className="max-h-36 overflow-y-auto">
                                        {statusOptions.filter(s => !creatableInput || s.includes(creatableInput.toLowerCase())).map(s => (
                                          <button key={s} onClick={() => saveInlineEdit(sub.id, 'status', s)} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 hover:text-blue-700">{s.replace(/_/g, ' ')}</button>
                                        ))}
                                        {creatableInput && !statusOptions.includes(creatableInput.toLowerCase().replace(/\s+/g,'_')) && (
                                          <button onClick={() => { const val = creatableInput.trim().toLowerCase().replace(/\s+/g,'_'); setStatusOptions(prev => [...prev, val]); saveInlineEdit(sub.id, 'status', val); }} className="w-full text-left px-2 py-1.5 text-xs rounded bg-green-50 text-green-700 font-medium">+ Criar "{creatableInput}"</button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div onClick={e => openInlineEdit(e, sub.id, 'status', sub.column_values?.status)} title="Clique para editar"
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 min-w-[90px] justify-center ${subStatusStyle.bg} ${subStatusStyle.text}`}>
                                    {subStatusStyle.label}
                                  </div>
                                </div>
                              );
                              if (colKey === 'person') return (
                                <div key={`sub-person-${sub.id}`} className="px-4 py-2 border-r border-gray-200 flex items-center justify-center">
                                  {(() => {
                                    const assignedPersons = sub.column_values?.persons || [];
                                    const assignedPerson = sub.column_values?.person || sub.column_values?.person_id;
                                    const responsibleList = assignedPersons.length > 0
                                      ? assignedPersons
                                      : (assignedPerson && assignedPerson !== '' && assignedPerson !== 'undefined')
                                        ? [{ name: assignedPerson, id: sub.column_values?.person_id || assignedPerson }]
                                        : [];
                                    return responsibleList.length > 0 ? (
                                      <div className="flex items-center space-x-1">
                                        <div className="flex -space-x-1">
                                          {responsibleList.slice(0, 2).map((person, idx) => (
                                            <div key={person.id || `sp${idx}`}
                                              className={`w-7 h-7 ${avatarColor(person.name, person.id)} rounded-full border-2 border-white flex items-center justify-center text-[10px] text-white font-bold cursor-pointer hover:ring-2 hover:ring-blue-300`}
                                              title={`${displayName(person.name, person.id)} — clique para gerenciar`}
                                              onClick={e => { e.stopPropagation(); handleAssignMember(sub.id); }}>
                                              {avatarInitials(person.name, person.id)}
                                            </div>
                                          ))}
                                          {responsibleList.length > 2 && (
                                            <div className="w-7 h-7 bg-gray-400 rounded-full border-2 border-white flex items-center justify-center text-xs text-white font-medium cursor-pointer"
                                              onClick={e => { e.stopPropagation(); handleAssignMember(sub.id); }}>
                                              +{responsibleList.length - 2}
                                            </div>
                                          )}
                                        </div>
                                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-xs text-white cursor-pointer hover:bg-green-600"
                                          onClick={e => { e.stopPropagation(); handleAssignMember(sub.id); }} title="Adicionar responsável">+</div>
                                      </div>
                                    ) : (
                                      <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-500 cursor-pointer hover:bg-blue-200"
                                        onClick={e => { e.stopPropagation(); handleAssignMember(sub.id); }} title="Atribuir responsável">+</div>
                                    );
                                  })()}
                                </div>
                              );
                              if (colKey === 'date') return (
                                <div key={`sub-date-${sub.id}`} className="px-3 py-2 border-r border-gray-200 flex items-center">
                                  {editingCell?.itemId === sub.id && editingCell?.field === 'due_date' ? (
                                    <input type="date" autoFocus value={editingValue} onChange={e => setEditingValue(e.target.value)}
                                      onBlur={e => { if (e.target.value) saveInlineEdit(sub.id, 'due_date', e.target.value); else setEditingCell(null); }}
                                      onKeyDown={e => { if (e.key === 'Enter') saveInlineEdit(sub.id, 'due_date', editingValue); if (e.key === 'Escape') setEditingCell(null); }}
                                      onClick={e => e.stopPropagation()}
                                      className="border border-blue-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 w-full" />
                                  ) : (
                                    <span onClick={e => openInlineEdit(e, sub.id, 'due_date', sub.column_values?.due_date || '')} title="Clique para editar"
                                      className={`cursor-pointer rounded px-2 py-1 text-xs font-medium hover:ring-1 hover:ring-blue-300 ${sub.column_values?.due_date ? 'bg-green-100 text-green-800' : 'text-gray-400'}`}>
                                      {sub.column_values?.due_date ? new Date(sub.column_values.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '+ Data'}
                                    </span>
                                  )}
                                </div>
                              );
                              if (colKey === 'priority') return (
                                <div key={`sub-priority-${sub.id}`} className="px-2 py-2 border-r border-gray-200 flex items-center relative">
                                  {editingCell?.itemId === sub.id && editingCell?.field === 'priority' && (
                                    <div className="absolute z-30 bg-white border border-gray-200 rounded-xl shadow-lg p-1 min-w-[150px]" style={{ top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
                                      <input autoFocus value={creatableInput} onChange={e => setCreatableInput(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter' && creatableInput.trim()) { const val = creatableInput.trim().toLowerCase().replace(/\s+/g,'_'); if (!priorityOptions.includes(val)) setPriorityOptions(prev => [...prev, val]); saveInlineEdit(sub.id, 'priority', val); }
                                          if (e.key === 'Escape') setEditingCell(null);
                                        }}
                                        placeholder="Buscar ou criar..." className="w-full px-2 py-1 text-xs border-b border-gray-100 mb-1 focus:outline-none" />
                                      <div className="max-h-36 overflow-y-auto">
                                        {priorityOptions.filter(p => !creatableInput || p.includes(creatableInput.toLowerCase())).map(p => (
                                          <button key={p} onClick={() => saveInlineEdit(sub.id, 'priority', p)} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 hover:text-blue-700">
                                            {p === 'alta' ? '🔴' : p === 'media' ? '🟡' : p === 'baixa' ? '🟢' : p === 'critica' ? '🚨' : '⚪'} {p.replace(/_/g,' ')}
                                          </button>
                                        ))}
                                        {creatableInput && !priorityOptions.includes(creatableInput.toLowerCase().replace(/\s+/g,'_')) && (
                                          <button onClick={() => { const val = creatableInput.trim().toLowerCase().replace(/\s+/g,'_'); setPriorityOptions(prev => [...prev, val]); saveInlineEdit(sub.id, 'priority', val); }} className="w-full text-left px-2 py-1.5 text-xs rounded bg-green-50 text-green-700 font-medium">+ Criar "{creatableInput}"</button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div onClick={e => openInlineEdit(e, sub.id, 'priority', sub.column_values?.priority)} title="Clique para editar"
                                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium cursor-pointer hover:opacity-80 ${
                                      sub.column_values?.priority === 'alta' || sub.column_values?.priority === 'critica' ? 'bg-red-100 text-red-800' :
                                      sub.column_values?.priority === 'media' ? 'bg-yellow-100 text-yellow-800' :
                                      sub.column_values?.priority === 'baixa' ? 'bg-green-100 text-green-800' : 'text-gray-400'
                                    }`}>
                                    {sub.column_values?.priority
                                      ? `${sub.column_values.priority === 'alta' ? '🔴' : sub.column_values.priority === 'media' ? '🟡' : sub.column_values.priority === 'baixa' ? '🟢' : sub.column_values.priority === 'critica' ? '🚨' : '⚪'} ${sub.column_values.priority.replace(/_/g,' ')}`
                                      : '+ Prioridade'}
                                  </div>
                                </div>
                              );
                              return null;
                            })}

                            {/* Colunas personalizadas */}
                            {customColumns && customColumns.length > 0 ? customColumns.map((column, idx) => (
                              <div key={`sub-cell-${column.id}-${sub.id}-${idx}`}
                                className="px-4 py-2 border-r border-gray-200 flex items-center cursor-pointer hover:bg-blue-50"
                                title={`${column.title}: ${renderCustomColumnValue(sub, column) || 'Vazio'}`}
                                onClick={e => handleCustomColumnClick(e, sub, column)}
                              >
                                <div className="text-sm truncate w-full">{renderCustomColumnValue(sub, column) || '-'}</div>
                              </div>
                            )) : null}

                            {/* Ações */}
                            <div className="px-2 py-2 flex items-center justify-center">
                              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 h-6 w-6 p-0" title="Mais opções">⋯</Button>
                            </div>
                          </div>
                        );
                        })}
                        {addingSubtask === item.id ? (
                          <div className="flex items-center pl-10 pr-4 py-2 space-x-2">
                            <span className="text-gray-300 text-xs">↳</span>
                            <input
                              autoFocus
                              className="flex-1 border border-blue-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                              placeholder="Nome da sub-tarefa..."
                              value={newSubtaskName}
                              onChange={e => setNewSubtaskName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleAddSubtask(item.id);
                                if (e.key === 'Escape') { setAddingSubtask(null); setNewSubtaskName(''); }
                              }}
                            />
                            <button onClick={() => handleAddSubtask(item.id)} className="text-xs bg-blue-500 text-white px-2 py-1 rounded-lg hover:bg-blue-600">Salvar</button>
                            <button onClick={() => { setAddingSubtask(null); setNewSubtaskName(''); }} className="text-xs text-gray-500 hover:text-gray-700">Cancelar</button>
                          </div>
                        ) : (
                          <button
                            className="w-full text-left pl-12 pr-4 py-1.5 text-xs text-gray-400 hover:text-blue-500 hover:bg-blue-50"
                            onClick={e => { e.stopPropagation(); setAddingSubtask(item.id); }}
                          >
                            + Adicionar sub-tarefa
                          </button>
                        )}
                      </div>
                    )}
                    </React.Fragment>
                  );
                })}

                {/* Botão adicionar item */}
                <div className="px-4 py-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedGroup(group.id)}
                      className="text-gray-500 hover:text-blue-600 text-sm"
                    >
                      + Adicionar item
                    </Button>
                  </div>
                  
                  {selectedGroup === group.id && (
                    <div className="mt-2 flex items-center space-x-2">
                      <Input
                        placeholder="Digite o nome do item..."
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddItem(group.id, newItem);
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAddItem(group.id, newItem)}
                        disabled={!newItem.trim()}
                      >
                        Adicionar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedGroup(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Estado vazio quando não há grupos */}
        {groupedItems.length === 0 && (
          <div className="p-8 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum grupo encontrado</h3>
              <p className="text-gray-600 mb-4">
                Comece criando um grupo para organizar suas tarefas ou adicione uma tarefa diretamente.
              </p>
              <div className="flex items-center justify-center space-x-3">
                <Button
                  onClick={() => handleAddNewGroup()}
                  variant="outline"
                >
                  + Criar Grupo
                </Button>
                <Button
                  onClick={() => handleAddNewTask()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  + Adicionar Tarefa
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <BoardStatusManager open={showStatusManager} onOpenChange={setShowStatusManager} api={api} boardId={board?.id} statuses={boardStatuses} />

      {/* Modal de comentários */}
      <Dialog open={showCommentsModal} onOpenChange={open => {
        if (open) { setShowCommentsModal(true); return; }
        // O AlertDialog de confirmação já está tratando o fechamento — se o
        // fechamento do AlertDialog reemitir um sinal de "fechar" pra esse
        // Dialog de fora (Radix propaga isso ao devolver o foco), ignoramos
        // aqui. Sem essa guarda, esse onOpenChange chamava requestCommentsModalClose()
        // de novo e reabria o aviso de "Descartar alterações?" logo depois de
        // "Continuar editando" fechá-lo, exigindo clicar duas vezes.
        // O Radix também emite "fechar" por perda de foco no instante em que o
        // aviso desmonta (showDiscardChangesDialog já voltou a false), por isso a
        // janela de tempo abaixo além do estado.
        if (showDiscardChangesDialog || Date.now() - discardDialogClosedAtRef.current < 500) return;
        requestCommentsModalClose();
      }}>
        <DialogContent
          overlayClassName="bg-slate-950/35 backdrop-blur-sm"
          className={`flex h-[88vh] max-h-[88vh] w-[94vw] max-w-none flex-col overflow-hidden rounded-2xl p-0 shadow-2xl sm:h-[85vh] sm:max-h-[85vh] sm:w-[85vw] ${darkMode ? 'nexo-project-dark bg-slate-950' : 'bg-slate-50'}`}
        >
          <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-6 py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <DialogTitle className="text-sm font-semibold text-slate-500">Detalhe da tarefa</DialogTitle>
              <div id="item-editor-header-actions" className="flex flex-wrap items-center gap-2 sm:mr-8" />
              {(itemEditorSyncing || boardRefreshing) && (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
                  Sincronizando listagem...
                </div>
              )}
            </div>
          </DialogHeader>
          
          {selectedItemForComments && (
            <ModernItemEditor
              item={selectedItemForComments}
              api={api}
              members={members}
              allItems={allDependencyItems}
              subtasks={subtasksData[selectedItemForComments.id] || []}
              parentItem={selectedItemForComments.parent_id ? items.find(i => i.id === selectedItemForComments.parent_id) : null}
              customColumns={customColumns}
              darkMode={darkMode}
              onClose={requestCommentsModalClose}
              onOpenItem={handleItemClick}
              onDirtyChange={setItemEditorDirty}
              onItemUpdate={async (updatedItem) => {
                // Salvar um campo não deve fechar o detalhe. Mantemos o item
                // selecionado sincronizado e atualizamos a lista em segundo plano.
                if (updatedItem?.id) {
                  setSelectedItemForComments(current => current?.id === updatedItem.id ? { ...current, ...updatedItem } : current);

                  const isSubtask = Boolean(updatedItem.parent_id) || Object.values(subtasksData).some(
                    subs => subs.some(subtask => subtask.id === updatedItem.id)
                  );

                  if (isSubtask) {
                    setSubtasksData(previous => {
                      const next = { ...previous };
                      for (const parentId of Object.keys(next)) {
                        const subtaskIndex = next[parentId].findIndex(subtask => subtask.id === updatedItem.id);
                        if (subtaskIndex !== -1) {
                          next[parentId] = [...next[parentId]];
                          next[parentId][subtaskIndex] = {
                            ...next[parentId][subtaskIndex],
                            ...updatedItem,
                            column_values: {
                              ...(next[parentId][subtaskIndex]?.column_values || {}),
                              ...(updatedItem.column_values || {}),
                            },
                          };
                          break;
                        }
                      }
                      return next;
                    });
                  } else {
                    onItemPatched?.(updatedItem);
                  }
                }

                setItemEditorSyncing(true);
                try {
                  await onDataChange?.();
                } finally {
                  setItemEditorSyncing(false);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* AlertDialog (não Dialog aninhado) de propósito: dois <Dialog> do Radix
          empilhados compartilham a mesma pilha de "camada dispensável", e
          fechar o de dentro podia acabar fechando também o de fora — fazendo
          parecer que só "Descartar" funcionava e "Continuar editando" não
          voltava pra edição. AlertDialog foi feito justamente para confirmar
          uma ação em cima de um modal já aberto, sem esse conflito. */}
      <AlertDialog open={showDiscardChangesDialog} onOpenChange={open => {
        if (!open) discardDialogClosedAtRef.current = Date.now();
        setShowDiscardChangesDialog(open);
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              Existem alterações não salvas nesta tarefa. Se você fechar agora, essas mudanças serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscardChangesDialog(false)}>Continuar editando</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 text-white hover:bg-red-700" onClick={closeCommentsModal}>Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showNewTaskModal} onOpenChange={setShowNewTaskModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova tarefa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nome da tarefa</label><Input autoFocus className="mt-2" placeholder="Ex.: Validar proposta comercial" value={newTaskName} onChange={e => setNewTaskName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleCreateTaskFromModal(); }} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Grupo do projeto</label><select className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={newTaskGroupId} onChange={e => setNewTaskGroupId(e.target.value)}>{groups.map(group => <option key={group.id} value={group.id}>{group.title || group.name}</option>)}</select></div>
            <p className="text-xs text-slate-500">Depois de criar, você poderá definir status, prazo, responsáveis, dependências e subtarefas no detalhe gerencial.</p>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowNewTaskModal(false)}>Cancelar</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateTaskFromModal}>Criar tarefa</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={showNewGroupModal} onOpenChange={setShowNewGroupModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo grupo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nome do grupo</label>
            <Input
              autoFocus
              placeholder="Ex.: Planejamento"
              value={newGroupName}
              onChange={event => setNewGroupName(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') handleCreateGroupFromModal(); }}
            />
            <p className="text-xs text-slate-500">Use grupos para organizar as etapas ou frentes de trabalho do projeto.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNewGroupModal(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateGroupFromModal}>Criar grupo</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showNewSubtaskModal} onOpenChange={setShowNewSubtaskModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova subtarefa</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subtarefa de</label>
            <p className="truncate text-sm font-medium text-slate-800">{items.find(item => item.id === subtaskParentId)?.name || 'Tarefa selecionada'}</p>
            <label className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Nome da subtarefa</label>
            <Input
              autoFocus
              placeholder="Ex.: Revisar materiais com o cliente"
              value={newSubtaskName}
              onChange={event => setNewSubtaskName(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') handleCreateSubtaskFromModal(); }}
            />
            <p className="text-xs text-slate-500">Ela herda o contexto da tarefa pai e pode receber prazo e responsáveis próprios no detalhe.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNewSubtaskModal(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateSubtaskFromModal}>Criar subtarefa</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de atribuição de responsável */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Atribuir Responsável</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Tarefa: <strong>{selectedItemForAssign?.name}</strong>
            </div>

            {/* Responsáveis atuais com opção de remover */}
            {(() => {
              const currentPersons = selectedItemForAssign?.column_values?.persons || [];
              const singlePerson = selectedItemForAssign?.column_values?.person;
              const displayList = currentPersons.length > 0
                ? currentPersons
                : (singlePerson && singlePerson !== '' && singlePerson !== 'undefined')
                  ? [{ name: singlePerson, id: selectedItemForAssign?.column_values?.person_id || singlePerson }]
                  : [];
              if (displayList.length === 0) return null;
              return (
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Responsáveis atuais</div>
                  {displayList.map((person, idx) => (
                    <div key={person.id || `dp${idx}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                      <div className="flex items-center space-x-2">
                        <div className={`w-7 h-7 ${avatarColor(person.name, person.id)} rounded-full flex items-center justify-center text-white text-[10px] font-bold`}>
                          {avatarInitials(person.name, person.id)}
                        </div>
                        <span className="text-sm text-gray-800 font-medium">{displayName(person.name, person.id)}</span>
                      </div>
                      <button
                        onClick={() => removeMemberFromItem(person.id, person.name)}
                        className="text-gray-400 hover:text-red-500 transition-colors px-1 py-0.5 rounded hover:bg-red-50 text-xs"
                        title="Remover responsável">
                        ✕ Remover
                      </button>
                    </div>
                  ))}
                  <div className="border-t border-gray-100 pt-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Adicionar membro</div>
                </div>
              );
            })()}

            {assignableMembers.length > 0 && (
              <Input
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Buscar por nome ou e-mail..."
                autoFocus
              />
            )}

            {assignableMembers.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredAssignableMembers.length === 0 && (
                  <div className="py-4 text-center text-sm text-gray-400">Nenhum membro encontrado</div>
                )}
                {filteredAssignableMembers.map((member) => {
                  const memberName = member.user_name || member.name || member.user_email || member.email || 'Membro sem nome';
                  const memberEmail = member.user_email || member.email || '';
                  const currentPersons = selectedItemForAssign?.column_values?.persons || [];
                  const memberId = member.user_id || member.id || member.user_email || member.email;
                  const alreadyAdded = currentPersons.some(p => p.name === memberName || p.id === memberId);
                  return (
                    <button
                      key={memberId}
                      onClick={() => !alreadyAdded && assignMemberToItem(memberId)}
                      disabled={alreadyAdded}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg border transition-colors ${alreadyAdded ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed' : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer'}`}
                    >
                      <div className={`w-8 h-8 ${avatarColor(memberName, memberId)} rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {avatarInitials(memberName, memberId)}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-gray-900">{displayName(memberName, memberId)}</div>
                        {memberEmail && memberEmail !== memberName && (
                          <div className="text-xs text-gray-500">{memberEmail}</div>
                        )}
                      </div>
                      {alreadyAdded && <span className="text-xs text-green-600 font-medium">✓ já adicionado</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">Nenhum membro encontrado</div>
                <div className="text-sm text-gray-500">Adicione membros ao quadro primeiro</div>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>Fechar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Delete Confirmation Modal */}
      {deletingGroup && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40"
          onClick={() => setDeletingGroup(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-5 w-80"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-800">Excluir grupo</h3>
            </div>
            <p className="text-xs text-gray-500 mb-1">Tem certeza que deseja excluir o grupo:</p>
            <p className="text-sm font-medium text-gray-800 mb-4 truncate">"{deletingGroup.title}"</p>
            <p className="text-xs text-red-500 mb-4">Esta ação é irreversível e removerá todos os itens deste grupo.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeletingGroup(null)}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteGroup}
                className="text-xs bg-red-500 text-white px-4 py-1.5 rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
